import React from 'react';
import {
  ArrowTrendingUpIcon,
  ArrowsRightLeftIcon,
  BanknotesIcon,
  BookOpenIcon,
  BriefcaseIcon,
  BuildingOffice2Icon,
  CalculatorIcon,
  ChartBarIcon,
  CheckBadgeIcon,
  CircleStackIcon,
  ClipboardDocumentCheckIcon,
  ClipboardDocumentIcon,
  ClipboardDocumentListIcon,
  CubeIcon,
  CurrencyDollarIcon,
  DocumentChartBarIcon,
  DocumentCheckIcon,
  DocumentDuplicateIcon,
  DocumentMagnifyingGlassIcon,
  DocumentPlusIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
  MapPinIcon,
  MegaphoneIcon,
  PencilSquareIcon,
  PresentationChartBarIcon,
  PuzzlePieceIcon,
  ReceiptPercentIcon,
  RectangleStackIcon,
  ScaleIcon,
  SparklesIcon,
  SquaresPlusIcon,
  TableCellsIcon
} from '@heroicons/react/24/outline';

import {
  CaraWorkspaceOutputFormat,
  CaraWorkspacePreset,
  CaraWorkspaceUseCase,
  DocumentType
} from '~/api/graphql';
import { acordDocumentOptions } from '~/components/presets/acord-document-options';
import { type UseCase } from '~/components/presets/presets.utils';
import { type TemplateLimit } from '~/components/presets/template-limit';

/** Icon-chip tint only — primary on this page means "yours / selected". */
export type UseCaseCategory = 'analyze' | 'verify' | 'produce';

export const USE_CASE_CATEGORIES: Record<
  UseCaseCategory,
  { label: string; chip: string; description: string }
> = {
  analyze: {
    label: 'Analysis',
    chip: 'bg-indigo-50 dark:bg-indigo-500/10 text-indigo-700 dark:text-indigo-300',
    description: 'Reads the documents and reports what it found.'
  },
  verify: {
    label: 'Verification',
    chip: 'bg-teal-50 dark:bg-teal-500/10 text-teal-700 dark:text-teal-300',
    description: 'Checks one thing against another and flags the gaps.'
  },
  produce: {
    label: 'Generation',
    chip: 'bg-amber-50 dark:bg-amber-500/10 text-amber-700 dark:text-amber-300',
    description: 'Fills or writes a document ready to send.'
  }
};

/** A task carries every book it serves; most P&C work serves both of theirs. */
export type LineOfBusiness = 'personal' | 'commercial' | 'benefits';

export const LINES_OF_BUSINESS: Record<LineOfBusiness, { label: string }> = {
  personal: { label: 'Personal' },
  commercial: { label: 'Commercial' },
  benefits: { label: 'Employee Benefits' }
};

/**
 * Filter order, taken from the record so a new book cannot reach the catalog
 * without reaching the filter that hides everything tagged with it.
 */
export const LINE_ORDER = Object.keys(LINES_OF_BUSINESS) as LineOfBusiness[];

/** Suggestion-flow axis: what kind of work this is. */
export type WorkloadTask =
  | 'checkDocuments'
  | 'fillForms'
  | 'clientDocuments'
  | 'findMarket'
  | 'answerCoverage'
  | 'spreadsheets'
  | 'claims';

/** Suggestion-flow axis: when in an account's life this runs. */
export type WorkMoment = 'newBusiness' | 'renewal' | 'service' | 'claims' | 'clientMeetings';

export type SourceLaneSpec = {
  label: string;
  description: string;
  /**
   * Whether an empty lane holds the run back. Omit for a lane the answer cannot
   * be built without. `recommended` is for one the run survives without at real
   * cost to the answer. Kept out of `description` so the card can render it as
   * status rather than spending the first words of every explanation on it.
   */
  requirement?: 'recommended' | 'optional';
  /** Prompt-read file-name tag. Must match the per-preset dialogs or Cara sees the wrong role. */
  filePrefix: string;
  /** Clone this lane; `filePrefix` gets a 1-based index appended. */
  repeatable?: boolean;
  /**
   * Boxes to open with. Two only where the repeatable lane is the whole card and
   * the task is a comparison, so it does not open asking for one of a pair.
   * Where another lane already holds the baseline, one box plus the add button
   * reads as one option and room for more, rather than a pair of empty slots.
   */
  repeatInitial?: number;
  /**
   * Message line `<key>: yes|no`, answered by whether this lane ended up with
   * files. Benefits prompts gate a whole section on the answer — without the
   * bound rates there is no expected premium, so no variance and no credit to
   * ask for — and a prompt cannot see the file names to work it out itself.
   */
  suppliedMessageKey?: string;
};

export type ExtraFieldSpec = {
  key: string;
  label: string;
  description?: string;
  multiline?: boolean;
  half?: boolean;
  requiresDocuments?: DocumentType[];
  /** Message-body heading. Omit to send `label: value`. */
  messageHeading?: string;
  /** Wire name for the `key: value` line, where the prompt's matcher and the label differ. */
  messageKey?: string;
  /** Sent when the field is empty, matching the per-preset dialog defaults. */
  emptyFallback?: string;
  /**
   * Renders a select rather than a text box, for a field whose prompt matcher
   * accepts a fixed set of values. An empty `value` sends no line at all, which
   * is how a reader says they do not know.
   */
  choices?: Array<{ value: string; label: string }>;
};

export type RunOptionSpec = {
  key: string;
  label: string;
  description: string;
  defaultOn: boolean;
  /** Message line `<messageKey>: enabled|disabled`. Prompts parse these, so the words matter. */
  messageKey: string;
};

export type RunScopeSpec = {
  key: string;
  label: string;
  description: string;
  messageKey: string;
  options: Array<{ value: string; label: string }>;
  defaults: string[];
};

export type RunCardTemplateKind = 'word' | 'html' | 'excel' | 'customForm';

export type DeliverableChoice = {
  key: string;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  outputFormat?: CaraWorkspaceOutputFormat;
  /** Message body `<messageKey>: <messageValue>`. */
  messageValue?: string;
  /** Word and HTML share one picker; the pick decides the format. */
  template?: boolean;
  /**
   * `filePrefix` of the lane holding the file this output is shaped from. Without
   * that file the prompt falls back to the source's own columns, so the choice
   * cannot be honoured and submit stays blocked.
   */
  requiresLane?: string;
  /** Restrict the picker. Omit to offer every library. */
  kinds?: RunCardTemplateKind[];
};

export type DeliverableSpec = {
  label: string;
  description: string;
  choices: DeliverableChoice[];
  defaultKey?: string;
  messageKey?: string;
  /**
   * Risk-report prompts match from the start of the body; SOV does not.
   * The wrong position silently drops the choice.
   */
  messagePosition?: 'leading' | 'trailing';
};

export type DocumentPickerScope = 'all' | 'certificates' | 'idCards';

export type UseCaseSpec = {
  id: CaraWorkspaceUseCase;
  preset: CaraWorkspacePreset;
  title: string;
  description: string;
  category: UseCaseCategory;
  lines: LineOfBusiness[];
  tasks: WorkloadTask[];
  moments: WorkMoment[];
  about: { summary: string; examples: string[] };
  icon: React.ComponentType<{ className?: string }>;
  pro?: boolean;
  sources: SourceLaneSpec[];
  instructions: { label: string; placeholder: string };
  deliverable?: DeliverableSpec;
  documents?: {
    scope: DocumentPickerScope;
    label: string;
    /** A function when the copy names how many documents the caller may pick. */
    description: string | ((limit: TemplateLimit) => string);
    placeholder: string;
    requiredError: string;
  };
  fields?: ExtraFieldSpec[];
  options?: RunOptionSpec[];
  scope?: RunScopeSpec;
  presetMetadata?: Record<string, unknown>;
  /**
   * Prompt-parsed body lines. `position` is load-bearing: some matchers are
   * anchored to the start of the body.
   */
  messageLines?: Array<{ key: string; value: string; position: 'leading' | 'trailing' }>;
  defaultMessage?: string;
  bodyPrefix?: string;
  requires?: 'sources' | 'instructions';
  /** Per-agency gate. `unavailable` hides the use case for everyone. */
  requiresLibrary?: RunCardTemplateKind[];
  /** Deep-link target for a retired id; search still emits the old vocabulary. */
  supersededBy?: CaraWorkspaceUseCase;
  /** Hidden from the home page and browser; kept so the enum and deep links still resolve. */
  unavailable?: string;
};

export const USE_CASE_FAMILIES: Array<{
  preset: CaraWorkspacePreset;
  title: string;
  description: string;
}> = [
  {
    preset: CaraWorkspacePreset.CertificateReview,
    title: 'Document Review',
    description: 'Find missing, incorrect, or conflicting information before documents are sent'
  },
  {
    preset: CaraWorkspacePreset.CoverageComparison,
    title: 'Compare & Verify',
    description: 'Show what changed across quotes, policies, and applications'
  },
  {
    preset: CaraWorkspacePreset.ContractReview,
    title: 'Contract Requirements',
    description: 'Show whether the current insurance program meets the contract requirements'
  },
  {
    preset: CaraWorkspacePreset.DocumentExploration,
    title: 'Explore a Policy',
    description: 'Ask Cara questions about a policy, quote, binder, or other document'
  },
  {
    preset: CaraWorkspacePreset.GenerateTemplate,
    title: 'Fill Forms & Documents',
    description: 'Fill ACORD forms and agency documents from existing account information'
  },
  {
    preset: CaraWorkspacePreset.GenerateQuoteProposal,
    title: 'Create Branded Document',
    description: 'Create a polished, client-facing document from quote or policy data'
  },
  {
    preset: CaraWorkspacePreset.CoverageGapAnalysis,
    title: 'Coverage Gap Analysis',
    description: 'Find important coverage gaps and recommend what to address first'
  },
  {
    preset: CaraWorkspacePreset.RiskMatching,
    title: 'Risk Matching',
    description: 'Rank potential carriers using the account details and agency appetite guidelines'
  },
  {
    preset: CaraWorkspacePreset.CommercialClassification,
    title: 'Classify Business',
    description: 'Find the NAICS, SIC, and GL codes that best match the actual operations'
  },
  {
    preset: CaraWorkspacePreset.UnderwritingAnalysis,
    title: 'Underwriting Analysis',
    description: 'Analyze applicant information for inconsistencies and underwriting issues'
  },
  {
    preset: CaraWorkspacePreset.SovAnalysis,
    title: 'SOV and COPE Analysis',
    description: 'Create a Statement of Values from account documents or public property data'
  },
  {
    preset: CaraWorkspacePreset.ClaimsReview,
    title: 'Claims Review',
    description: 'Assess claim coverage, next steps, and possible agency E&O issues'
  },
  {
    preset: CaraWorkspacePreset.LossRunAnalysis,
    title: 'Loss Run Analysis',
    description: 'Compile and research claim history for renewal discussions'
  },
  {
    preset: CaraWorkspacePreset.RiskReport,
    title: 'Risk Report',
    description: 'Client-facing risk report with industry research and mitigations'
  },
  {
    preset: CaraWorkspacePreset.EbQuoteAnalysis,
    title: 'Benefits Quotes',
    description: 'Compare every benefits quote using the same employee census'
  },
  {
    preset: CaraWorkspacePreset.EbContributionAnalysis,
    title: 'Benefits Contributions',
    description: 'Compare employer costs and employee payroll deductions under different strategies'
  },
  {
    preset: CaraWorkspacePreset.EbCensusNormalization,
    title: 'Benefits Census',
    description: 'Clean an employee census and map it onto the layout a platform or carrier wants'
  },
  {
    preset: CaraWorkspacePreset.EbBillingAudit,
    title: 'Benefits Billing Audit',
    description: 'Audit a benefits invoice against enrollment and the agreed rates'
  },
  {
    preset: CaraWorkspacePreset.EbClaimsReview,
    title: 'Benefits Claims Review',
    description: 'Explain claims cost trends, large-claim concentration, and stop-loss exposure'
  },
  {
    preset: CaraWorkspacePreset.EbDocumentReview,
    title: 'Benefits Document Review',
    description: 'Check benefits documents against the rates and plan documents behind them'
  },
  {
    preset: CaraWorkspacePreset.EbEnrollmentMaterials,
    title: 'Open Enrollment Materials',
    description: 'Create employee forms, guides, and announcements for open enrollment'
  },
  {
    preset: CaraWorkspacePreset.CommissionProcessing,
    title: 'Commission Statements',
    description: 'Standardize carrier statements, audit payments, and reconcile receipts'
  }
];

const NOTES = {
  label: 'Instructions',
  placeholder: 'Optional. anything Cara should know'
};

const QUOTE_OR_POLICY: SourceLaneSpec = {
  label: 'Quotes or policies',
  description: 'Anything Cara should compare the primary document against.',
  filePrefix: '[Quote or Policy] '
};

/** Exclusive: `outputFormat` is one value, so a workbook and a template cannot both be asked for. */
const COMPARISON_DELIVERABLE: DeliverableSpec = {
  label: 'Write-up',
  description: 'Choose how to receive the comparison.',
  choices: [
    {
      key: 'workspace',
      label: "Cara's write-up",
      icon: SparklesIcon,
      outputFormat: CaraWorkspaceOutputFormat.Text,
      description: 'The full comparison in the workspace.'
    },
    {
      key: 'xlsx',
      label: 'Excel workbook',
      icon: TableCellsIcon,
      outputFormat: CaraWorkspaceOutputFormat.Xlsx,
      description: 'A workbook built after the analysis. The full write-up stays in the workspace.'
    },
    {
      key: 'template',
      label: 'Agency template',
      icon: DocumentTextIcon,
      template: true,
      description: 'Cara fills the selected template and posts a short summary in the workspace.'
    }
  ],
  defaultKey: 'workspace'
};

/** Excel is requested via a parsed message line; the typed output format is unused. */
const SOV_DELIVERABLE: DeliverableSpec = {
  label: 'Format',
  description: 'Choose where to receive the Statement of Values.',
  choices: [
    {
      key: 'workspace',
      label: 'Table in the workspace',
      icon: SparklesIcon,
      messageValue: 'text',
      description: 'A markdown table, ready to read and copy.'
    },
    {
      key: 'xlsx',
      label: 'Excel file',
      icon: TableCellsIcon,
      messageValue: 'Excel',
      description: 'A spreadsheet. Takes longer to generate.'
    }
  ],
  defaultKey: 'workspace',
  messageKey: 'Output Format',
  messagePosition: 'trailing'
};

/** Single choice: the form is the deliverable, so only the picker renders. */
const CUSTOM_FORM_DELIVERABLE: DeliverableSpec = {
  label: 'Form',
  description: 'The form Cara fills, chosen from the agency library.',
  choices: [
    {
      key: 'template',
      label: 'From the form library',
      icon: DocumentPlusIcon,
      template: true,
      kinds: ['customForm'],
      description: 'Cara fills this form from the attached sources.'
    }
  ],
  defaultKey: 'template'
};

/** Always produces a document; this picks who lays it out, not whether there is one. */
const BRANDED_DOCUMENT_DELIVERABLE: DeliverableSpec = {
  label: 'Document',
  description: 'Choose whether Cara designs the document or fills an agency template.',
  choices: [
    {
      key: 'workspace',
      label: 'Cara designs it',
      icon: SparklesIcon,
      outputFormat: CaraWorkspaceOutputFormat.Text,
      description: 'A print-ready PDF, laid out by Cara.'
    },
    {
      key: 'template',
      label: 'From the agency library',
      icon: DocumentTextIcon,
      template: true,
      description: 'Cara fills the selected template or form.'
    }
  ],
  defaultKey: 'workspace'
};

/** Prompt matches `Output Format` at the start of the body. */
const RISK_REPORT_DELIVERABLE: DeliverableSpec = {
  label: 'Format',
  description: 'Choose how to receive the report.',
  choices: [
    {
      key: 'workspace',
      label: 'Summary in the workspace',
      icon: SparklesIcon,
      messageValue: 'text',
      description: 'Markdown, ready to read and copy.'
    },
    {
      key: 'pdf',
      label: 'PDF report',
      icon: DocumentChartBarIcon,
      messageValue: 'PDF',
      description: 'A client-ready report. Takes longer to generate.'
    }
  ],
  defaultKey: 'pdf',
  messageKey: 'Output Format',
  messagePosition: 'leading'
};

/** Factory so the three underwriting use cases do not share a mutable defaults array. */
const underwritingScope = (): RunScopeSpec => ({
  key: 'coverage',
  label: 'Coverage to review',
  description: 'Lines Cara should evaluate the application against.',
  messageKey: 'Coverage Information',
  options: [
    { value: 'Business Operations', label: 'Business operations (including liability)' },
    { value: 'Workers Compensation', label: 'Workers compensation' },
    { value: 'Property', label: 'Property' },
    { value: 'Auto', label: 'Auto' }
  ],
  defaults: ['Business Operations']
});

/**
 * Benefits prompts read `Output Format` off the message and build the workbook
 * themselves, so this carries no typed output format. A factory because the
 * default differs by task: a worksheet goes on a projector in front of the
 * employer, where an audit is read before anyone asks for a file.
 */
const benefitsWorkbook = (defaultKey: 'workspace' | 'xlsx'): DeliverableSpec => ({
  label: 'Format',
  description: 'Choose how to receive the results.',
  choices: [
    {
      key: 'workspace',
      label: 'Write-up in the workspace',
      icon: SparklesIcon,
      messageValue: 'text',
      description: 'Tables, ready to read and copy.'
    },
    {
      key: 'xlsx',
      label: 'Excel workbook',
      icon: TableCellsIcon,
      messageValue: 'Excel',
      description: 'Every figure with the arithmetic behind it. Takes longer to generate.'
    }
  ],
  defaultKey,
  messageKey: 'Output Format',
  messagePosition: 'trailing'
});

/**
 * Single catalog for the home page, browser, and run card.
 *
 * Copy contract:
 * - `description` states the business result in one plain-English sentence.
 * - `about.summary` leads with the result, explains what supports it, then names
 *   a material limitation only when it changes what the user can rely on.
 * - Source descriptions explain why an input matters, not how the prompt works.
 *
 * `filePrefix`, `messageHeading`, and `emptyFallback` must stay byte-for-byte
 * with the per-preset dialogs — the prompts match on those strings. So do
 * `suppliedMessageKey` and any `label` on a field the prompts parse.
 */
export const USE_CASES: UseCaseSpec[] = [
  // ── Document Review ────────────────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.ReviewCoiEoi,
    preset: CaraWorkspacePreset.CertificateReview,
    title: 'Review COI / EOI',
    description:
      'The review checks every certificate field against the policy behind it and rates the E&O exposure.',
    category: 'verify',
    lines: ['commercial'],
    tasks: ['checkDocuments'],
    moments: ['newBusiness', 'service'],
    about: {
      summary:
        'Identifies certificate errors before the document is sent and rates the agency’s E&O exposure. It compares the named insured, carrier, policy term, limits, certificate holder, additional insured status and waiver status with the supporting quote or policy. The results show every field that needs correction.',
      examples: [
        "A subcontractor's certificate checked against his GL and workers comp policies",
        "A certificate for a restaurant client's landlord, verified before the CSR sends it",
        'An evidence of property insurance for a loan closing, matched to mortgagee and limit'
      ]
    },
    icon: DocumentCheckIcon,
    sources: [
      {
        label: 'Certificate of insurance',
        description: 'The certificate or evidence of insurance under review.',
        filePrefix: '[COI/EOI] '
      },
      QUOTE_OR_POLICY
    ],
    instructions: NOTES
  },
  {
    id: CaraWorkspaceUseCase.ReviewAcords,
    preset: CaraWorkspacePreset.CertificateReview,
    title: 'Review ACORDs',
    description:
      'The review flags blanks, vague answers and conflicts on any ACORD application, and rates the E&O exposure.',
    category: 'verify',
    lines: ['personal', 'commercial'],
    tasks: ['checkDocuments'],
    moments: ['newBusiness'],
    about: {
      summary:
        'Identifies blanks, vague answers and conflicting information in any ACORD application before submission. The review checks details such as the insured, entity, address, operations, limits, schedules, prior losses and carrier information across the attached forms. Each form receives a table of issues and an E&O risk rating.',
      examples: [
        'A commercial package 125, 126 and 140 read over before it goes to three carriers',
        "A contractor's ACORD 130 whose class codes and payrolls should match the expiring policy",
        'A personal lines application taken over the phone, checked for blanks and vague answers'
      ]
    },
    icon: DocumentCheckIcon,
    sources: [
      {
        label: 'ACORD forms',
        description:
          'The ACORD forms to check for blanks, vague answers and conflicts. Any application form is read.',
        filePrefix: '[ACORD Form] '
      },
      {
        label: 'Supporting documents',
        description: 'Quotes, policies, or anything else to check the forms against.',
        filePrefix: '[Quote or Policy] '
      }
    ],
    instructions: NOTES
  },
  {
    id: CaraWorkspaceUseCase.ReviewSupplementals,
    preset: CaraWorkspacePreset.CertificateReview,
    title: 'Review supplementals',
    description:
      'The review flags every question on the carrier form left blank or answered too vaguely to underwrite.',
    category: 'verify',
    lines: ['personal', 'commercial'],
    tasks: ['checkDocuments'],
    moments: ['newBusiness'],
    about: {
      summary:
        'Identifies incomplete or unclear answers on carrier supplemental forms before they reach underwriting. Every question is checked for blanks, placeholder text and answers that lack enough detail. The results include an E&O risk rating, a table of questions that need attention and any conflicts with the attached application.',
      examples: [
        'A habitational supplement returned with the roof and wiring questions blank',
        'A cyber supplemental whose revenue and prior claims must agree with the ACORD 125',
        'A trucking driver and vehicle supplement checked against the expiring auto schedule'
      ]
    },
    icon: DocumentCheckIcon,
    sources: [
      {
        label: 'Supplemental documents',
        description: 'The supplemental or carrier-specific forms under review.',
        filePrefix: '[Supplemental] '
      },
      {
        label: 'Supporting documents',
        description: 'Policies, applications, or anything else to check the forms against.',
        filePrefix: '[Quote or Policy] '
      }
    ],
    instructions: NOTES
  },

  // ── Compare & Verify Coverage ─────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.CompareQuotes,
    preset: CaraWorkspacePreset.CoverageComparison,
    title: 'Compare quotes and policies',
    description:
      'The comparison marks each difference between the options better, worse or equivalent for the insured.',
    category: 'verify',
    lines: ['personal', 'commercial'],
    tasks: ['checkDocuments'],
    moments: ['newBusiness', 'clientMeetings'],
    about: {
      summary:
        'Shows which quote provides the stronger terms and where each option creates a trade-off for the insured. The comparison covers limits, deductibles, premium, exposure bases, carrier conditions and endorsements. Each difference is marked as better, worse or equivalent, followed by an E&O assessment and recommendation.',
      examples: [
        "Three carrier quotes on a restaurant's package and liquor liability",
        'An incumbent homeowners policy and two quotes, one with a percentage wind deductible',
        "A monoline GL quote plus umbrella measured against a competitor's package"
      ]
    },
    icon: DocumentDuplicateIcon,
    sources: [
      {
        label: 'Option',
        description:
          'The quotes or policies making up this option. Keep each option in its own box.',
        filePrefix: '[Option ',
        repeatable: true,
        repeatInitial: 2
      }
    ],
    instructions: NOTES,
    deliverable: COMPARISON_DELIVERABLE
  },
  {
    id: CaraWorkspaceUseCase.CompareRenewal,
    preset: CaraWorkspacePreset.CoverageComparison,
    title: 'Compare a renewal',
    description:
      'The comparison shows what moved from the expiring terms and recommends holding or shopping the account.',
    category: 'verify',
    lines: ['personal', 'commercial'],
    tasks: ['checkDocuments'],
    moments: ['renewal', 'clientMeetings'],
    about: {
      summary:
        'Shows what changed at renewal and whether the account should be retained as offered or marketed to other carriers. It compares the expiring policy with the renewal on premium, limits, deductibles, exposure bases, warranties and endorsements. Every change is cited, evaluated for the insured and included in the recommendation.',
      examples: [
        "A 22 percent increase on a manufacturer's property and GL renewal",
        'An endorsement on the expiring habitational policy missing from the renewal',
        'A personal auto renewal where deductibles, UM limits and the driver schedule changed'
      ]
    },
    icon: ArrowsRightLeftIcon,
    sources: [
      {
        label: 'Current policy',
        description: 'The expiring policy used as the point of comparison.',
        filePrefix: '[Option 1] '
      },
      {
        label: 'Renewal offer',
        description: 'The renewal quote or policy offered for the coming term.',
        filePrefix: '[Option 2] '
      }
    ],
    instructions: NOTES,
    deliverable: COMPARISON_DELIVERABLE
  },
  {
    id: CaraWorkspaceUseCase.CheckAgainstApplication,
    preset: CaraWorkspacePreset.CoverageComparison,
    title: 'Policy check',
    description:
      'The check finds every mismatch between what was submitted and what the carrier issued.',
    category: 'verify',
    lines: ['personal', 'commercial'],
    tasks: ['checkDocuments'],
    moments: ['newBusiness', 'service'],
    about: {
      summary:
        'Identifies every difference between the application and the quote or policy issued by the carrier. It checks the insured, addresses, class codes, payroll, schedules, limits, deductibles and policy dates. Each mismatch is listed with its level of E&O risk.',
      examples: [
        'A bound workers comp policy checked for the class codes and payroll submitted',
        'A commercial auto policy whose vehicle and driver schedules changed at binding',
        'A homeowners policy that should name the family trust the application named'
      ]
    },
    icon: ClipboardDocumentIcon,
    sources: [
      {
        label: 'Application',
        description: 'The application or other document that was submitted.',
        filePrefix: '[Option 1] '
      },
      {
        label: 'Policy',
        description: 'The quote or policy the carrier issued, checked against what was submitted.',
        filePrefix: '[Option 2] '
      }
    ],
    instructions: NOTES,
    deliverable: COMPARISON_DELIVERABLE
  },

  // ── Contract Requirements ─────────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.CompareLeaseToPolicy,
    preset: CaraWorkspacePreset.ContractReview,
    title: 'Compare a lease to a policy',
    description:
      'The review returns each lease requirement as Met, Partial or Failed, with the endorsement that closes it.',
    category: 'verify',
    lines: ['commercial'],
    tasks: ['checkDocuments', 'answerCoverage'],
    moments: ['service'],
    about: {
      summary:
        'Shows whether the insurance program satisfies each requirement in the lease. The review extracts requirements from the insurance, indemnity and general terms sections and compares them with the attached policies. Each item is marked Met, Partial or Failed, with the endorsement needed to close any gap.',
      examples: [
        "A retail tenant's package measured against the insurance article of a 40 page lease",
        'A GL endorsement that must name the owner and lender as primary and noncontributory',
        "A landlord's per location aggregate and 30 day notice terms after a carrier change"
      ]
    },
    icon: DocumentTextIcon,
    sources: [
      {
        label: 'Lease agreement',
        description: 'The lease whose insurance requirements Cara should extract.',
        filePrefix: '[Contract] '
      },
      {
        label: 'Insurance policies',
        description: 'The policies to compare against the lease requirements.',
        filePrefix: '[Quote or Policy] '
      }
    ],
    instructions: NOTES
  },
  {
    id: CaraWorkspaceUseCase.ReviewVendorContract,
    preset: CaraWorkspacePreset.ContractReview,
    title: 'Review a vendor contract',
    description:
      'The review tests each requirement in the subcontract against the policy and names where coverage falls short.',
    category: 'verify',
    lines: ['commercial'],
    tasks: ['checkDocuments', 'answerCoverage'],
    moments: ['service'],
    about: {
      summary:
        'Shows whether the insured’s policies satisfy the insurance requirements in a vendor or subcontractor agreement. The review considers both the stated requirements and the scope of work, then cites the policy form and page supporting each conclusion. The results identify the most important gaps and the endorsements needed to address them.',
      examples: [
        'Roofing subcontract where height and exterior-work exclusions may fail the GC terms',
        'Janitorial hospital vendor agreement demanding professional liability and a waiver',
        'Trucking shipper contract requiring follow-form excess and additional insured status'
      ]
    },
    icon: DocumentTextIcon,
    sources: [
      {
        label: 'Vendor contract',
        description: 'The agreement whose insurance requirements Cara should extract.',
        filePrefix: '[Contract] '
      },
      QUOTE_OR_POLICY
    ],
    instructions: NOTES
  },
  {
    id: CaraWorkspaceUseCase.ReviewCoiRequirements,
    preset: CaraWorkspacePreset.ContractReview,
    title: 'Review COI requirements',
    description:
      'The review separates the requirements the policy forms already support from those that need an endorsement.',
    category: 'verify',
    lines: ['commercial'],
    tasks: ['checkDocuments'],
    moments: ['service'],
    about: {
      summary:
        'Shows which certificate requirements the current policies support and which require a policy change. The review compares the requirements sheet, instruction list or contract language with the policies behind the certificate. Each result quotes the supporting policy language and identifies any endorsement needed before the certificate is issued.',
      examples: [
        'Insurance requirements sheet that arrived with a purchase order',
        'Rejected certificate a customer says is missing a waiver of subrogation',
        'Municipal bid packet limits and additional insured wording before the deadline'
      ]
    },
    icon: DocumentCheckIcon,
    sources: [
      {
        label: 'Requirements',
        description: 'The COI requirements list or the contract section it came from.',
        filePrefix: '[Contract] '
      },
      {
        label: 'Policy documents',
        description: 'The policies backing the certificate.',
        filePrefix: '[Quote or Policy] '
      }
    ],
    instructions: NOTES
  },

  // ── Explore a Policy ──────────────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.AskPolicyQuestion,
    preset: CaraWorkspacePreset.DocumentExploration,
    title: 'Ask a policy question',
    description:
      'Every question gets its own answer, quoting the policy language the answer rests on.',
    category: 'analyze',
    lines: ['personal', 'commercial'],
    tasks: ['answerCoverage'],
    moments: ['service', 'clientMeetings'],
    about: {
      summary:
        'Answers a specific coverage question using the language in the attached policy, quote, binder or endorsement. The response quotes the wording supporting the answer and identifies where it appears in the document. The level of detail follows the scope of the question.',
      examples: [
        "Coverage B limit for a homeowner's new detached workshop",
        'Hired and non-owned check on a commercial auto binder before a rental',
        'Ransomware waiting period and retention buried in a cyber quote'
      ]
    },
    icon: MagnifyingGlassIcon,
    sources: [
      {
        label: 'Policy or document',
        description:
          'The policy, quote, binder or endorsement package the question is about. Each one is searched on its own.',
        filePrefix: '[Policy] '
      }
    ],
    instructions: {
      label: 'Question',
      placeholder: 'Questions about these documents'
    }
  },
  {
    id: CaraWorkspaceUseCase.ExploreExclusions,
    preset: CaraWorkspacePreset.DocumentExploration,
    title: 'Explore exclusions',
    description:
      'The review pulls every exclusion in the policy and pairs it with the exceptions that narrow it.',
    category: 'analyze',
    lines: ['personal', 'commercial'],
    tasks: ['answerCoverage'],
    moments: ['service', 'clientMeetings'],
    about: {
      summary:
        'Shows which exclusions matter most to the insured’s operations or a specific claim scenario. The review follows the policy’s forms schedule, identifies each exclusion and includes any exception that restores coverage. The results rank the exclusions by relevance and evaluate them against any scenario provided.',
      examples: [
        "Height, EIFS, and subcontractor exclusions on a roofing contractor's renewal",
        'Denied water damage claim on a habitational package, checked against the forms',
        'What a wildfire-area dwelling policy will not pay, ahead of a renewal call'
      ]
    },
    icon: DocumentMagnifyingGlassIcon,
    sources: [
      {
        label: 'Policy',
        description: 'The policy whose exclusions Cara should walk.',
        filePrefix: '[Policy] '
      }
    ],
    instructions: {
      label: 'Areas to explore',
      placeholder: 'Optional. a scenario to evaluate each exclusion against'
    }
  },
  {
    id: CaraWorkspaceUseCase.SummarizeCoverage,
    preset: CaraWorkspacePreset.DocumentExploration,
    title: 'Summarize coverage',
    description:
      'Puts limits, deductibles, retentions and important conditions into one clear coverage table.',
    category: 'analyze',
    lines: ['personal', 'commercial'],
    tasks: ['answerCoverage'],
    moments: ['service', 'clientMeetings'],
    about: {
      summary:
        'Provides one clear summary of the policy’s limits, deductibles, retentions and important conditions. The review starts with the declarations and then checks the forms for sublimits, special deductibles and waiting periods not shown there. It flags coverage that appears absent and items that still require carrier confirmation.',
      examples: [
        'Coverage recap for a renewal meeting out of a ninety-page commercial package',
        "Every limit and deductible on a prospect's expiring policy before marketing it",
        'Wind and flood deductibles on a coastal dwelling policy, side by side'
      ]
    },
    icon: DocumentChartBarIcon,
    sources: [
      {
        label: 'Policy or quote',
        description:
          'The policy or quote to summarize. Include the forms and endorsements, since sublimits live there rather than on the dec page.',
        filePrefix: '[Policy] '
      }
    ],
    instructions: {
      label: 'Instructions',
      placeholder: 'Optional. lines of coverage or sections to focus on'
    }
  },

  // ── Fill Forms & Documents ────────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.FillAcordForm,
    preset: CaraWorkspacePreset.GenerateTemplate,
    title: 'Fill an ACORD form',
    description:
      'Cara fills the ACORD forms chosen for the account and cites the source behind every value it enters.',
    category: 'produce',
    lines: ['personal', 'commercial'],
    tasks: ['fillForms'],
    moments: ['newBusiness', 'service'],
    about: {
      summary:
        'Produces completed ACORD forms from the available account documents. It can use declarations, quotes, applications and intake notes to fill each field. The results identify the source of every value and clearly list missing or conflicting information.',
      examples: [
        'Handwritten renewal worksheet turned into an ACORD 125 and 126 for market',
        'ACORD 2 auto loss notice from adjuster intake notes and the dec page',
        'ACORD 80 homeowners application off the expiring carrier declarations'
      ]
    },
    icon: PencilSquareIcon,
    documents: {
      scope: 'all',
      label: 'Forms',
      description: ({ word }) => `Up to ${word} at a time. Cara fills each from the sources below.`,
      placeholder: 'Select forms',
      requiredError: 'Choose at least one form'
    },
    sources: [
      {
        label: 'Source documents',
        description: 'Anything Cara should read to fill the forms.',
        filePrefix: '[Input] '
      }
    ],
    fields: [
      {
        key: 'certificateHolder',
        label: 'Certificate holder',
        description: "Name and address. Defaults to the named insured's information.",
        multiline: true,
        requiresDocuments: [DocumentType.Acord_24, DocumentType.Acord_25],
        messageHeading: 'Certificate Holder',
        emptyFallback: "Use the named insured's information as the certificate holder."
      },
      {
        key: 'additionalInterest',
        label: 'Additional interest',
        description: 'Name and address. Defaults to whatever the documents say.',
        multiline: true,
        requiresDocuments: [DocumentType.Acord_27, DocumentType.Acord_28],
        messageHeading: 'Additional Interest',
        emptyFallback: 'Reference the documentation accordingly.'
      }
    ],
    instructions: {
      label: 'Instructions',
      placeholder: 'Optional. anything that should be mapped into the form'
    }
  },
  {
    id: CaraWorkspaceUseCase.GenerateCertificate,
    preset: CaraWorkspacePreset.GenerateTemplate,
    title: 'Generate COI / EOI',
    description: 'Produces a completed ACORD 24, 25, 27 or 28 from the selected policy or binder.',
    category: 'produce',
    lines: ['commercial'],
    tasks: ['fillForms'],
    moments: ['service'],
    about: {
      summary:
        'Produces a completed ACORD 24, 25, 27 or 28 from the selected binder or policy. It adds the certificate holder or additional interest, fills the coverage information and drafts the description of operations. Any additional insured or waiver status is tied to the supporting endorsement, and missing or conflicting information is identified for review.',
      examples: [
        "ACORD 25 for a general contractor's new sub before Monday's job start",
        'ACORD 28 evidence of property for a lender on a strip-mall refinance',
        'Reissued certificate showing the new term, carrier, and limits after renewal'
      ]
    },
    icon: CheckBadgeIcon,
    documents: {
      scope: 'certificates',
      label: 'Certificate',
      description: 'Which ACORD certificate to produce.',
      placeholder: 'Select a certificate',
      requiredError: 'Choose a certificate'
    },
    sources: [
      {
        label: 'Binder or policy',
        description: 'What Cara should pull the coverage details from.',
        filePrefix: '[Input] '
      }
    ],
    fields: [
      {
        key: 'certificateHolder',
        label: 'Certificate holder',
        half: true,
        requiresDocuments: [DocumentType.Acord_24, DocumentType.Acord_25],
        messageHeading: 'Certificate Holder',
        emptyFallback: "Use the named insured's information as the certificate holder."
      },
      {
        key: 'holderAddress',
        label: 'Holder address',
        half: true,
        requiresDocuments: [DocumentType.Acord_24, DocumentType.Acord_25],
        messageHeading: 'Certificate Holder Address'
      },
      {
        key: 'additionalInterest',
        label: 'Additional interest',
        half: true,
        requiresDocuments: [DocumentType.Acord_27, DocumentType.Acord_28],
        messageHeading: 'Additional Interest',
        emptyFallback: 'Reference the documentation accordingly.'
      },
      {
        key: 'additionalInterestAddress',
        label: 'Interest address',
        half: true,
        requiresDocuments: [DocumentType.Acord_27, DocumentType.Acord_28],
        messageHeading: 'Additional Interest Address'
      },
      {
        key: 'descriptionOfOperations',
        label: 'Description of operations',
        description: 'Leave blank to have Cara write it from the binder.',
        multiline: true,
        messageHeading: 'Description of Operations'
      }
    ],
    instructions: {
      label: 'Instructions',
      placeholder: 'Optional. anything else the certificate needs to say'
    }
  },
  {
    id: CaraWorkspaceUseCase.GenerateIdCards,
    preset: CaraWorkspacePreset.GenerateTemplate,
    title: 'Generate ID cards',
    description:
      'Cara reads the auto declarations and produces one ID card per vehicle on the policy.',
    category: 'produce',
    lines: ['personal'],
    tasks: ['fillForms'],
    moments: ['service'],
    about: {
      summary:
        'Produces one auto insurance ID card for each vehicle shown on the policy or binder. It fills the insured, policy number, term, carrier and vehicle information on the generic ACORD 50 or selected state form. Any missing information is identified for completion.',
      examples: [
        'Cards for an insured who bound personal auto this morning and needs proof today',
        'Replacement card after a second vehicle was added mid-term',
        'California ID card from renewal declarations with a new policy number'
      ]
    },
    icon: RectangleStackIcon,
    documents: {
      scope: 'idCards',
      label: 'ID cards',
      description: 'Which ID card to produce.',
      placeholder: 'Select an ID card',
      requiredError: 'Choose an ID card'
    },
    sources: [
      {
        label: 'Policy or binder',
        description: 'What Cara should pull vehicle and policy details from.',
        filePrefix: '[Input] '
      }
    ],
    instructions: NOTES
  },
  // Covered by "Fill a custom form"; a second tile onto the same library is a false choice.
  {
    id: CaraWorkspaceUseCase.FillDueDiligence,
    preset: CaraWorkspacePreset.GenerateTemplate,
    title: 'Fill a due diligence questionnaire',
    description:
      'Cara answers the questionnaire from the attached policies and lists any field it could not source.',
    category: 'produce',
    lines: ['commercial'],
    tasks: ['fillForms'],
    moments: ['service'],
    about: {
      summary:
        'Produces a completed due diligence questionnaire using information supported by the attached policies. It fills carrier, limit, date and additional insured details and lists any fields that still need an answer.',
      examples: [
        "Annual vendor insurance questionnaire from a client's largest customer",
        'Landlord due diligence packet on limits and additional insured status before a lease',
        'Franchisor compliance questionnaire at renewal with answers across three carriers'
      ]
    },
    icon: ClipboardDocumentIcon,
    unavailable: 'Upload the questionnaire to the form library, then fill it from there.',
    sources: [
      {
        label: 'Source documents',
        description: 'Policies and anything else holding the answers.',
        filePrefix: '[Input] '
      }
    ],
    instructions: NOTES
  },
  {
    id: CaraWorkspaceUseCase.FillOtherPdf,
    preset: CaraWorkspacePreset.GenerateTemplate,
    title: 'Fill a custom form',
    description:
      'Cara fills an agency form from the attached documents and calls out every blank it left.',
    category: 'produce',
    lines: ['personal', 'commercial'],
    tasks: ['fillForms'],
    moments: ['service'],
    about: {
      summary:
        'Produces a completed agency form using the supporting documents attached to the run. Each field is filled from a source such as a policy, quote, declaration or intake sheet. The results show what was entered, where it came from and which fields remain blank.',
      examples: [
        "Carrier restaurant supplemental from an ACORD 125 and last year's policy",
        'State workers comp form for a new class code off the current declarations',
        'MGA excess and surplus submission for a habitational account from loss runs and an SOV'
      ]
    },
    icon: DocumentPlusIcon,
    requiresLibrary: ['customForm'],
    deliverable: CUSTOM_FORM_DELIVERABLE,
    sources: [
      {
        label: 'Source documents',
        description:
          'Anything holding the answers: policies, quotes, dec pages or an intake sheet.',
        filePrefix: '[Input] '
      }
    ],
    instructions: NOTES
  },

  // ── Create Branded Document ───────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.CreateQuoteProposal,
    preset: CaraWorkspacePreset.GenerateQuoteProposal,
    title: 'Build a quote proposal',
    description:
      'The proposal presents the options side by side and marks the trade-offs, exactly as quoted.',
    category: 'produce',
    lines: ['personal', 'commercial'],
    tasks: ['clientDocuments'],
    moments: ['newBusiness', 'clientMeetings'],
    about: {
      summary:
        'Creates a client-ready proposal that compares carrier options without changing the quoted terms. It presents premiums, limits, sublimits and deductibles side by side and highlights the important trade-offs. The proposal includes an executive summary and recommended next steps, while missing or conflicting information is identified rather than filled in.',
      examples: [
        "Three carriers' package quotes for a contractor weighing price against limits",
        'A monoline cyber quote for a client who has never bought the coverage',
        'Two habitational property options where one carries a separate wind deductible'
      ]
    },
    icon: CurrencyDollarIcon,
    sources: [
      {
        label: 'Option',
        description:
          'The quotes or binders making up this option. Keep each option in its own box.',
        filePrefix: '[Option ',
        repeatable: true,
        repeatInitial: 2
      }
    ],
    instructions: {
      label: 'Instructions',
      placeholder: 'Optional. how the proposal should read'
    },
    deliverable: BRANDED_DOCUMENT_DELIVERABLE,
    presetMetadata: { generateQuoteProposalDocumentType: 'quote' }
  },
  {
    id: CaraWorkspaceUseCase.CreateRenewalProposal,
    preset: CaraWorkspacePreset.GenerateQuoteProposal,
    title: 'Build a renewal proposal',
    description:
      'The proposal walks the renewing terms line by line, separating what changed from what held.',
    category: 'produce',
    lines: ['personal', 'commercial'],
    tasks: ['clientDocuments'],
    moments: ['renewal', 'clientMeetings'],
    about: {
      summary:
        'Creates a client-ready renewal proposal showing what changed and what remained the same. It compares the expiring policy with the renewal quote or binder on premium, limits, deductibles, carrier and coverage forms. The source documents remain separate so the comparison does not mix terms between periods, and missing or conflicting information is identified rather than assumed.',
      examples: [
        'A 14% increase on a commercial package renewal where the limits never moved',
        'A homeowners renewal that switched carriers and raised the wind deductible',
        'Renewal review prep for a workers comp account before the meeting with the insured'
      ]
    },
    icon: ArrowTrendingUpIcon,
    sources: [
      {
        label: 'Current policy',
        description: 'The expiring policy the proposal measures the renewal against.',
        filePrefix: '[Current Policy] '
      },
      {
        label: 'Proposed option',
        description: 'The renewal quote or binder the proposal presents.',
        filePrefix: '[Proposed Option] '
      }
    ],
    instructions: {
      label: 'Instructions',
      placeholder: 'Optional. how the proposal should read'
    },
    deliverable: BRANDED_DOCUMENT_DELIVERABLE,
    presetMetadata: { generateQuoteProposalDocumentType: 'renewal' }
  },
  {
    id: CaraWorkspaceUseCase.CreateSummaryDocument,
    preset: CaraWorkspacePreset.GenerateQuoteProposal,
    title: 'Build a summary or notice',
    description:
      'The document explains the coverage in plain language, with every figure taken from the sources.',
    category: 'produce',
    lines: ['personal', 'commercial'],
    tasks: ['clientDocuments'],
    moments: ['service', 'clientMeetings'],
    about: {
      summary:
        'Creates a plain-language client summary or notice from the attached policy, binder or endorsement. Every figure, form number and date comes from the source documents. Any information needed for the requested document but missing from the sources is clearly identified.',
      examples: [
        "One-page coverage summary for a new commercial client's file",
        'Written notice of what a new protective safeguards endorsement requires',
        "A personal umbrella's underlying limit requirements in client language"
      ]
    },
    icon: DocumentTextIcon,
    sources: [
      {
        label: 'Source documents',
        description: 'The policies, quotes or correspondence the document should be built from.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: {
      label: 'Instructions',
      placeholder: 'Optional. what the summary should cover'
    },
    deliverable: BRANDED_DOCUMENT_DELIVERABLE,
    presetMetadata: { generateQuoteProposalDocumentType: 'summary' }
  },
  {
    id: CaraWorkspaceUseCase.CreateOtherDocument,
    preset: CaraWorkspacePreset.GenerateQuoteProposal,
    title: 'Build a custom document',
    description: 'Cara writes a custom client-facing document, grounded in the attached sources.',
    category: 'produce',
    lines: ['personal', 'commercial'],
    tasks: ['clientDocuments'],
    moments: ['service', 'clientMeetings'],
    about: {
      summary:
        'Creates a custom client-facing document following the requested content and structure. Every name, number and factual statement is grounded in the attached source documents. Missing information is identified rather than assumed.',
      examples: [
        'Stewardship report for a mid-size account ahead of the annual meeting',
        "Claims reporting instruction sheet for the insured's location managers",
        'Pre-renewal exposure worksheet for a client to complete before the meeting'
      ]
    },
    icon: SparklesIcon,
    sources: [
      {
        label: 'Source documents',
        description:
          'Whatever the document should be built from. Every name and number is taken from these.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: {
      label: 'Instructions',
      placeholder: 'What the document should cover'
    },
    deliverable: BRANDED_DOCUMENT_DELIVERABLE,
    presetMetadata: { generateQuoteProposalDocumentType: '' }
  },

  // ── Coverage Gap Analysis ─────────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.FullGapAnalysis,
    preset: CaraWorkspacePreset.CoverageGapAnalysis,
    title: 'Full gap analysis',
    description:
      'The gap report covers limit changes, exclusions worth buying back, lines to add and claim scenarios.',
    category: 'analyze',
    lines: ['personal', 'commercial'],
    tasks: ['answerCoverage'],
    moments: ['renewal', 'clientMeetings'],
    about: {
      summary:
        'Identifies the most important gaps in the current insurance program and recommends what to address first. The report combines the attached policies or quotes with research on the insured’s locations, values and operations. It covers limit changes, exclusions to remove, missing locations, additional lines of coverage, possible markets and realistic claim scenarios. Without current policies or quotes, it cannot measure the coverage already in place.',
      examples: [
        'Commercial renewal prep for an owner asking what else they should carry',
        "A prospect's expiring homeowners, auto, and umbrella declarations at broker-of-record",
        "First read of an inherited contractor's GL, property, and workers comp program"
      ]
    },
    icon: PuzzlePieceIcon,
    sources: [
      {
        label: 'Policies or quotes',
        requirement: 'recommended',
        description:
          'The policies or quotes in force today. Without them there is no coverage for the run to measure against.',
        filePrefix: '[Policy Document] '
      },
      {
        label: 'Supporting documents',
        requirement: 'optional',
        description: 'Applications or anything describing the operations.',
        filePrefix: '[Supporting Document] '
      }
    ],
    instructions: NOTES
  },
  {
    id: CaraWorkspaceUseCase.ReviewCurrentCoverage,
    preset: CaraWorkspacePreset.CoverageGapAnalysis,
    title: 'Review current coverage',
    description:
      'Summarizes the current coverage, then identifies important gaps and realistic claim scenarios.',
    category: 'analyze',
    lines: ['personal', 'commercial'],
    tasks: ['answerCoverage'],
    moments: ['service', 'clientMeetings'],
    about: {
      summary:
        'Explains the coverage currently in force and identifies limits or additional lines to consider. The review covers limits, sublimits, deductibles, valuation terms, exclusions, endorsements and scheduled property, with emphasis on the selected concerns.',
      examples: [
        "Learning what a manufacturer's property and GL say before the first client call",
        'Checking homeowners sublimits for jewelry, fine arts, and water backup',
        'Reading a claims-made professional liability form for its retroactive date'
      ]
    },
    icon: DocumentChartBarIcon,
    unavailable: 'Summarize coverage covers the same ground.',
    supersededBy: CaraWorkspaceUseCase.SummarizeCoverage,
    sources: [
      {
        label: 'Policies or quotes',
        requirement: 'recommended',
        description:
          'The policies or quotes in force today. Without them there is no coverage for the run to measure against.',
        filePrefix: '[Policy Document] '
      },
      {
        label: 'Additional documents',
        requirement: 'optional',
        description: 'Schedules or endorsements not in the policy.',
        filePrefix: '[Supporting Document] '
      }
    ],
    instructions: {
      label: 'Focus areas',
      placeholder: 'Optional. coverage areas or concerns to prioritize'
    }
  },
  {
    id: CaraWorkspaceUseCase.ReviewClaimScenarios,
    preset: CaraWorkspacePreset.CoverageGapAnalysis,
    title: 'Review claim scenarios',
    description:
      'The gap report traces losses that would fall outside the forms, alongside the full coverage review.',
    category: 'analyze',
    lines: ['personal', 'commercial'],
    tasks: ['answerCoverage'],
    moments: ['clientMeetings'],
    about: {
      summary:
        'Shows how realistic losses could expose weaknesses in the current insurance program. The report includes two to five claim scenarios and explains which policy form or endorsement would limit or exclude coverage in each case. Every scenario is rated for severity and likelihood as part of the full coverage-gap review.',
      examples: [
        'Showing a restaurant owner the cost of a fire with business income capped at 12 months',
        "A trucking client's exposure for delivery runs in an employee's personal vehicle",
        'Making the case for standalone cyber to a distributor with a data exclusion on GL'
      ]
    },
    icon: ExclamationTriangleIcon,
    sources: [
      {
        label: 'Policies or quotes',
        requirement: 'recommended',
        description:
          'The policies or quotes in force today. Without them there is no coverage for the run to measure against.',
        filePrefix: '[Policy Document] '
      },
      {
        label: 'Additional documents',
        requirement: 'optional',
        description:
          'Applications, contracts or anything else describing the operations the scenarios are drawn against.',
        filePrefix: '[Supporting Document] '
      }
    ],
    instructions: {
      label: 'Client context',
      placeholder: 'Optional. the business and any known exposures'
    }
  },

  // ── Risk Matching ─────────────────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.PlaceNewSubmission,
    preset: CaraWorkspacePreset.RiskMatching,
    title: 'Place a new submission',
    description:
      'The placement report profiles the risk, ranks the markets and lists what underwriting will ask.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['findMarket'],
    moments: ['newBusiness'],
    about: {
      summary:
        'Identifies the carriers most likely to consider a new commercial submission and explains why. The report combines the submission details with class-code research and the agency’s appetite guidelines. Each market is ranked with supporting evidence and the questions underwriting is likely to ask.',
      examples: [
        'A roofing contractor GL and property submission the standard markets may decline',
        'Multi-state staffing workers comp, and which carriers write the class in each state',
        'An architecture firm professional liability with only the application in hand'
      ]
    },
    icon: BriefcaseIcon,
    sources: [
      {
        label: 'Submission documents',
        description: 'Applications, loss runs, and anything describing the risk.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: NOTES,
    options: [
      {
        key: 'publicData',
        label: 'Verify against public data',
        description: 'Adds a few minutes and cites every source.',
        defaultOn: true,
        messageKey: 'Public Data Verification and Enrichment'
      }
    ],
    defaultMessage: 'Extract the risk details and identify market placement options.'
  },
  {
    id: CaraWorkspaceUseCase.FindAlternativeCarriers,
    preset: CaraWorkspacePreset.RiskMatching,
    title: 'Find alternative carriers',
    description:
      'The placement report ranks every market in the appetite guidelines and reviews the class codes.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['findMarket'],
    moments: ['renewal'],
    about: {
      summary:
        'Identifies alternative carriers for a commercial account and explains which markets are the strongest fit. The report reviews the current policy, application and loss history, then applies the agency’s appetite guidelines to the account’s class codes, operations, revenue and payroll. Every market considered is listed with its ranking and supporting reasoning.',
      examples: [
        'A restaurant renewal that came back up forty percent and the client wants options',
        'A commercial auto risk being non-renewed after two at-fault losses',
        'Testing a long-held habitational property program against other markets'
      ]
    },
    icon: BuildingOffice2Icon,
    sources: [
      {
        label: 'Risk documents',
        description: 'The current policy, loss runs, and application.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: NOTES,
    options: [
      {
        key: 'publicData',
        label: 'Verify against public data',
        description: 'Adds a few minutes and cites every source.',
        defaultOn: true,
        messageKey: 'Public Data Verification and Enrichment'
      }
    ],
    defaultMessage: 'Extract the risk details and identify market placement options.'
  },
  {
    id: CaraWorkspaceUseCase.ResearchAlternativeClassifications,
    preset: CaraWorkspacePreset.RiskMatching,
    title: 'Research alternative classifications',
    description:
      'The placement report reviews the class codes and the markets that alternate codes would open.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['findMarket'],
    moments: ['newBusiness', 'renewal'],
    about: {
      summary:
        'Shows whether a more accurate business classification could improve market access. The report identifies a primary code and up to two alternatives for NAICS, ISO general liability and state workers compensation. It also shows which markets in the agency’s appetite guidelines become available under each supported classification.',
      examples: [
        'A landscaper coded as tree trimming and shut out of the standard markets',
        'A machine shop doing field installation, where payroll split picks the GL class',
        'A workers comp account whose assigned NCCI code looks wrong for the operations'
      ]
    },
    icon: CubeIcon,
    sources: [
      {
        label: 'Risk documents',
        description: 'Applications or anything describing the operations.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: NOTES,
    options: [
      {
        key: 'publicData',
        label: 'Verify against public data',
        description: 'Adds a few minutes and cites every source.',
        defaultOn: true,
        messageKey: 'Public Data Verification and Enrichment'
      }
    ],
    defaultMessage: 'Extract the risk details and identify market placement options.'
  },

  // ── Classify Business ─────────────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.ClassifyNaics,
    preset: CaraWorkspacePreset.CommercialClassification,
    title: 'Classify by NAICS',
    description:
      'Identifies the most appropriate NAICS code based on the business’s actual operations and revenue.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['findMarket'],
    moments: ['newBusiness'],
    about: {
      summary:
        'Identifies the NAICS code that best reflects how the business operates and earns its revenue. The analysis uses the attached application or business description together with public information. It returns a primary code, one to three supported alternatives, source links and a confidence rating.',
      examples: [
        'A metal fabrication submission split between shop work and on-site installation',
        'A restaurant group with catering and a retail bakery counter on the application',
        'A carrier rejecting a renewal because the code on file is too broad'
      ]
    },
    icon: SquaresPlusIcon,
    sources: [
      {
        label: 'Source documents',
        description: 'Applications or anything describing the operations.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: {
      label: 'Business description',
      placeholder: 'Optional. Describe the operations'
    },
    messageLines: [{ key: 'Classification Type', value: 'NAICS', position: 'leading' }]
  },
  {
    id: CaraWorkspaceUseCase.ClassifySic,
    preset: CaraWorkspacePreset.CommercialClassification,
    title: 'Classify by SIC',
    description:
      'The research returns the SIC code a carrier worksheet needs, with the evidence behind it.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['findMarket'],
    moments: ['newBusiness'],
    about: {
      summary:
        'Identifies the SIC code that best reflects the business’s actual operations. The analysis compares the attached documents with the company website, directory listings and registration records. It returns a primary code, supported alternatives for meaningful secondary operations, source links and a confidence rating.',
      examples: [
        'A staffing agency submission going to a market whose worksheet takes only SIC',
        'A commercial printer that moved into packaging after the policy code was set',
        'A purchased prospect list whose vendor-supplied codes look wrong for the operations'
      ]
    },
    icon: SquaresPlusIcon,
    sources: [
      {
        label: 'Source documents',
        description: 'Applications or anything describing the operations.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: {
      label: 'Business description',
      placeholder: 'Optional. Describe the operations'
    },
    messageLines: [{ key: 'Classification Type', value: 'SIC', position: 'leading' }]
  },
  {
    id: CaraWorkspaceUseCase.ClassifyIsoGl,
    preset: CaraWorkspacePreset.CommercialClassification,
    title: 'Classify by ISO GL code',
    description:
      'The research returns the GL class the operations support, not the one the business name suggests.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['findMarket'],
    moments: ['newBusiness'],
    about: {
      summary:
        'Identifies the ISO general liability class that best matches the business’s actual operations. The analysis uses the attached documents and public information such as service pages, directory listings and reviews. It returns a primary class, supported alternatives for operations representing a meaningful share of revenue, source links and a confidence rating.',
      examples: [
        'A landscaper adding hardscaping and snow plowing under a lawn maintenance class',
        'An underwriter challenging the class on a bar and grill with more food than liquor',
        'A janitorial account going to a new market that needs the class to hold up first'
      ]
    },
    icon: SquaresPlusIcon,
    sources: [
      {
        label: 'Source documents',
        description: 'Applications or anything describing the operations.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: {
      label: 'Business description',
      placeholder: 'Optional. Describe the operations'
    },
    messageLines: [{ key: 'Classification Type', value: 'GL', position: 'leading' }]
  },
  {
    id: CaraWorkspaceUseCase.VerifyClassification,
    preset: CaraWorkspacePreset.CommercialClassification,
    // Sent the same message and rendered the same prompt as the NAICS tile, which
    // gets only the classification type. It also promised a comparison against the
    // code on file, and the prompt has no notion of one.
    unavailable: 'Classify by NAICS runs the same research.',
    supersededBy: CaraWorkspaceUseCase.ClassifyNaics,
    title: 'Verify a classification',
    description:
      'Identifies the NAICS code supported by the business’s operations and explains the evidence.',
    category: 'verify',
    lines: ['commercial'],
    tasks: ['checkDocuments', 'findMarket'],
    moments: ['renewal', 'service'],
    about: {
      summary:
        'Provides the NAICS code supported by the business’s operations, along with alternatives, sources and a confidence rating. It runs the same research as Classify by NAICS and does not compare the result with a code already on file.',
      examples: [
        "A carrier audit questioning the code on a wholesaler's policy",
        'A renewal call where the insured mentions new installation work',
        'An inherited account whose dec page code does not match the company website'
      ]
    },
    icon: CheckBadgeIcon,
    sources: [
      {
        label: 'Source documents',
        description: 'The policy or application carrying the current classification.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: {
      label: 'Business description',
      placeholder: 'Optional. Describe the operations'
    },
    messageLines: [{ key: 'Classification Type', value: 'NAICS', position: 'leading' }]
  },
  {
    id: CaraWorkspaceUseCase.BulkClassReview,
    preset: CaraWorkspacePreset.CommercialClassification,
    title: 'Bulk class review',
    description:
      'The review returns one row per account: the code on file, the code supported and the evidence.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['spreadsheets', 'findMarket'],
    moments: ['service'],
    about: {
      summary:
        'Shows which accounts in a book of business may carry the wrong NAICS code. Each business is researched separately, and the results list the current code, the supported code and the evidence for any change. The summary also shows how many codes were retained or changed and highlights patterns across the book.',
      examples: [
        'A carrier class-code audit landing on an entire commercial package book',
        'Codes inherited with an acquired agency and never checked against operations',
        'A producer prospect list carrying industry codes from the lead vendor'
      ]
    },
    icon: TableCellsIcon,
    sources: [
      {
        label: 'Book of business',
        description: 'A spreadsheet or export with one row per account.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: NOTES,
    messageLines: [{ key: 'Classification Type', value: 'NAICS', position: 'leading' }]
  },

  // ── Underwriting Analysis (Pro) ───────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.ReviewApplication,
    preset: CaraWorkspacePreset.UnderwritingAnalysis,
    title: 'Review an application',
    description:
      'Identifies missing, inconsistent or unsupported information in a commercial insurance application.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['checkDocuments'],
    moments: ['newBusiness'],
    about: {
      summary:
        'Identifies the application issues most likely to affect underwriting before the submission is sent. The review compares the application, supplemental forms, loss runs and financial records with each other and with public information such as the company website, news reports and registration records. Every finding is rated critical, warning or clear and includes the stated value, the verified value, its source and the documentation needed next.',
      examples: [
        'A fire found in local news that never appeared on the applicant’s loss runs',
        'A restaurant submission whose stated revenue looks low for the square footage',
        'A trucking renewal where loss runs and the application disagree on fleet size'
      ]
    },
    icon: DocumentMagnifyingGlassIcon,
    pro: true,
    sources: [
      {
        label: 'Application documents',
        description: 'The application and everything submitted with it.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: NOTES,
    scope: underwritingScope()
  },
  {
    id: CaraWorkspaceUseCase.VerifyInsuredInfo,
    preset: CaraWorkspacePreset.UnderwritingAnalysis,
    title: 'Verify insured info',
    description:
      'The check marks address, revenue, headcount and ownership as verified, conflicting or unverifiable.',
    category: 'verify',
    lines: ['commercial'],
    tasks: ['checkDocuments'],
    moments: ['newBusiness'],
    about: {
      summary:
        'Shows which important facts about the insured can be verified and which need follow-up. The review checks the address, years in business, revenue, employee count and ownership against state registrations, address records and industry benchmarks. Each item is marked verified, conflicting or unverifiable and includes its source.',
      examples: [
        'A new commercial account whose application address is a mailbox store',
        "A general contractor's claimed years in business, against the state registration",
        'An insured who mentioned another company of theirs on a renewal call'
      ]
    },
    icon: GlobeAltIcon,
    pro: true,
    sources: [
      {
        label: 'Supporting documents',
        description:
          'Anything carrying the named insured, entity type, address and operations to verify.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: NOTES,
    scope: underwritingScope()
  },
  {
    id: CaraWorkspaceUseCase.InvestigatePotentialMisrep,
    preset: CaraWorkspacePreset.UnderwritingAnalysis,
    // Sent the same message and rendered the same prompt as the application review, which
    // already runs the website, news and registration checks this tile named. Misrepresentation
    // is also a conclusion the prompt never reaches: it rates findings for investigation.
    unavailable: 'Review an application runs the same checks.',
    supersededBy: CaraWorkspaceUseCase.ReviewApplication,
    title: 'Investigate possible misrep',
    description:
      'Ranks conflicting or unsupported application details and identifies what documentation to request next.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['checkDocuments'],
    moments: ['claims', 'newBusiness'],
    about: {
      summary:
        'Identifies application details that require further investigation and the documents needed for follow-up. It compares the application with websites, news, reviews, registration records and payroll benchmarks, then rates each concern critical, warning or clear. It does not determine intent or conclude that a misrepresentation occurred.',
      examples: [
        'A workers comp account whose reported payroll sits below the trade wage',
        'A property submission claiming minimal flood exposure on a Zone A building',
        "A fire found in local news that never appeared on the applicant's loss runs"
      ]
    },
    icon: ExclamationTriangleIcon,
    pro: true,
    sources: [
      {
        label: 'Application documents',
        description: 'The application and anything that contradicts it.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: NOTES,
    scope: underwritingScope()
  },

  // ── SOV and COPE Analysis (Pro) ───────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.SovDocumentOnly,
    preset: CaraWorkspacePreset.SovAnalysis,
    title: 'Build an SOV from documents',
    description:
      'The workbook assembles one SOV from the attached documents and lists every field they left unanswered.',
    category: 'produce',
    lines: ['commercial'],
    tasks: ['spreadsheets'],
    moments: ['newBusiness', 'renewal'],
    about: {
      summary:
        'Creates one Statement of Values from the available ACORD forms, applications and property schedules. Each location or building includes the available construction, occupancy, protection and exposure details. Missing information is listed for follow-up instead of being estimated, and no public-record research is performed.',
      examples: [
        'One clean values page from three years of ACORD 140s and a landlord schedule',
        'Per-building rollup of an apartment portfolio from the expiring property schedule',
        'Strip mall values from a prior agent spreadsheet and two appraisals, conflicts flagged'
      ]
    },
    icon: TableCellsIcon,
    pro: true,
    sources: [
      {
        label: 'Source documents',
        description: 'ACORD forms, prior applications, schedules.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: NOTES,
    deliverable: SOV_DELIVERABLE,
    messageLines: [{ key: 'Web Search and Verification', value: 'disabled', position: 'trailing' }],
    defaultMessage: 'Create a Statement of Values and COPE analysis from the provided documents.'
  },
  {
    id: CaraWorkspaceUseCase.SovDocumentsAndWeb,
    preset: CaraWorkspacePreset.SovAnalysis,
    title: 'Build an SOV and enrich it',
    description:
      'The workbook adds public records for year built, construction and flood to what the documents supply.',
    category: 'produce',
    lines: ['commercial'],
    tasks: ['spreadsheets'],
    moments: ['newBusiness', 'renewal'],
    about: {
      summary:
        'Creates a more complete Statement of Values by adding public property information to the uploaded documents. Each address is researched for year built, construction, fire protection, flood zone, occupancy and value. The results include sources for the added information and can be delivered as workspace tables or an Excel workbook.',
      examples: [
        'Construction type, roof age, and sprinkler status for a five-building industrial park',
        'Ground-floor retail tenant roll for a mixed-use submission',
        'Owner-reported values on a 40-unit apartment checked against assessment data'
      ]
    },
    icon: GlobeAltIcon,
    pro: true,
    sources: [
      {
        label: 'Source documents',
        description: 'ACORD forms, prior applications, schedules.',
        filePrefix: '[Input Document] '
      }
    ],
    instructions: {
      label: 'Addresses and instructions',
      placeholder: 'Optional. addresses to research, and anything else to consider'
    },
    deliverable: SOV_DELIVERABLE,
    messageLines: [{ key: 'Web Search and Verification', value: 'enabled', position: 'trailing' }],
    defaultMessage: 'Create a Statement of Values and COPE analysis from the provided documents.'
  },
  {
    id: CaraWorkspaceUseCase.SovAddressOnly,
    preset: CaraWorkspacePreset.SovAnalysis,
    title: 'Research addresses into an SOV',
    description:
      'An address list is the only input, and the research fills in full COPE detail from public records.',
    category: 'produce',
    lines: ['commercial'],
    tasks: ['spreadsheets'],
    moments: ['newBusiness'],
    about: {
      summary:
        'Creates a preliminary Statement of Values from an address list when no property documents are available. Public records are used to research buildings, construction, fire protection, flood zone, occupancy and value for each location. The results include sources and can be delivered as workspace tables or an Excel workbook.',
      examples: [
        'Four addresses a prospect emailed before any paperwork exists',
        'Values page for a newly built retail center with no application on file',
        'Flood zone and fire protection class on two warehouses bought mid-term'
      ]
    },
    icon: MapPinIcon,
    pro: true,
    sources: [],
    instructions: {
      label: 'Addresses',
      placeholder: 'One per line, or in any format'
    },
    // Addresses are the whole input; an empty brief used to send "Research the following addresses" alone.
    requires: 'instructions',
    deliverable: SOV_DELIVERABLE,
    messageLines: [{ key: 'Web Search and Verification', value: 'enabled', position: 'trailing' }],
    defaultMessage: 'Research the following addresses and create a Statement of Values.'
  },

  // ── Claims Review (Pro) ───────────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.ReviewClaimViability,
    preset: CaraWorkspacePreset.ClaimsReview,
    title: 'Review claim viability',
    description:
      'Shows how the policy may respond to the claim and quotes the language supporting the conclusion.',
    category: 'analyze',
    lines: ['personal', 'commercial'],
    tasks: ['claims', 'answerCoverage'],
    moments: ['claims'],
    about: {
      summary:
        'Shows how the attached policy may respond to the claim and what should happen next. The review compares the loss notice, adjuster correspondence and any denial with the declarations, forms and endorsements. It quotes the relevant coverage language, identifies applicable limits and highlights possible E&O concerns. A declarations page alone cannot support a full coverage review because the policy forms determine how coverage applies.',
      examples: [
        'Homeowners water loss the carrier is denying under the seepage exclusion',
        'Subcontractor injury tendered to a GL policy under the action-over exclusion',
        'Business income denial turning on how the form defines period of restoration'
      ]
    },
    icon: ChartBarIcon,
    pro: true,
    sources: [
      {
        label: 'Claim documents',
        description: 'The loss notice, adjuster correspondence, and denials.',
        filePrefix: '[Claim Document] '
      },
      {
        label: 'Policy documents',
        description:
          'Declarations, forms and endorsements. The forms decide coverage, so a dec page on its own limits the read.',
        filePrefix: '[Policy Document] '
      },
      {
        label: 'Supporting documents',
        requirement: 'optional',
        description: 'The original application and anything else relevant.',
        filePrefix: '[Application Document] '
      }
    ],
    instructions: NOTES
  },
  {
    id: CaraWorkspaceUseCase.ReviewClaimPlanNextSteps,
    preset: CaraWorkspacePreset.ClaimsReview,
    title: 'Plan next steps on a claim',
    description: 'The plan orders the next moves: obligations, deadlines, tender and subrogation.',
    category: 'analyze',
    lines: ['personal', 'commercial'],
    tasks: ['claims'],
    moments: ['claims'],
    about: {
      summary:
        'Provides an ordered action plan for moving the claim forward. The report uses the current claim status, notices, policies and any reservation of rights to identify outstanding duties and deadlines. It then addresses tender, subrogation, appraisal, possible E&O concerns and situations that may require coverage counsel.',
      examples: [
        'Property claim where the adjuster has gone quiet for six weeks',
        'Reply to a reservation of rights letter on a commercial auto claim',
        'Slip and fall where two policies could respond and someone must tender first'
      ]
    },
    icon: ArrowTrendingUpIcon,
    pro: true,
    sources: [
      {
        label: 'Claim documents',
        description: 'The loss notice, adjuster correspondence, and denials.',
        filePrefix: '[Claim Document] '
      },
      {
        label: 'Policy documents',
        description: 'The policies involved, for coverage context.',
        filePrefix: '[Policy Document] '
      },
      {
        label: 'Prior correspondence',
        requirement: 'optional',
        description: 'Reservation of rights letters and other history.',
        filePrefix: '[Application Document] '
      }
    ],
    instructions: {
      label: 'Current claim status',
      placeholder: 'Where the claim stands, and what needs deciding'
    }
  },
  {
    id: CaraWorkspaceUseCase.CheckEoRisk,
    preset: CaraWorkspacePreset.ClaimsReview,
    title: 'Check new claim E&O risk',
    description:
      'The review names where the agency is exposed, from coverage never offered to schedules never updated.',
    category: 'verify',
    lines: ['personal', 'commercial'],
    tasks: ['claims', 'checkDocuments'],
    moments: ['claims'],
    about: {
      summary:
        'Identifies facts in a new claim that may create E&O exposure for the agency. The review compares the claim file with the policy as issued and the application signed at binding. It highlights issues such as unscheduled exposures, coverage not offered in writing, missing additional insured or waiver requirements and inconsistencies in the original application. Without the application records, it cannot fully compare the claim with what was disclosed when coverage was placed.',
      examples: [
        'First-party fire loss on a building added mid-term and never scheduled',
        'A denial for a missing additional insured endorsement the certificate showed',
        'A large new-account loss reviewed before the file reaches the agency E&O carrier'
      ]
    },
    icon: ExclamationTriangleIcon,
    pro: true,
    sources: [
      {
        label: 'Claim documents',
        description: 'The loss notice, adjuster correspondence, and denials.',
        filePrefix: '[Claim Document] '
      },
      {
        label: 'Policy documents',
        description:
          'Declarations, forms and endorsements. The forms decide coverage, so a dec page on its own limits the read.',
        filePrefix: '[Policy Document] '
      },
      {
        label: 'Application documents',
        requirement: 'optional',
        description:
          'The application and anything else representing the risk at bind. Without it the claim cannot be tested against what was disclosed.',
        filePrefix: '[Application Document] '
      }
    ],
    instructions: NOTES
  },

  // ── Loss Run Analysis (Pro) ───────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.CompileLossHistory,
    preset: CaraWorkspacePreset.LossRunAnalysis,
    title: 'Compile loss history',
    description:
      'The register pulls every claim off the loss runs and aggregates them by year and line.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['spreadsheets', 'claims'],
    moments: ['renewal'],
    about: {
      summary:
        'Creates one reliable loss-history register from all available loss runs. Each claim includes the date, cause, carrier, line of coverage, paid amount, reserve, total incurred and status, with totals checked against the source reports. The results include summaries by year and line of coverage plus a statement of relevant risk controls.',
      examples: [
        'Five years of workers comp and GL loss runs from three carriers, one exhibit',
        'Incurred by year for a trucking account before the experience mod moves',
        'Submission prep with one open reserve that will have to be explained'
      ]
    },
    icon: CircleStackIcon,
    pro: true,
    sources: [
      {
        label: 'Loss runs',
        description: 'Every loss run on file for the account.',
        filePrefix: '[Loss Run] '
      },
      {
        label: 'Supporting documents',
        requirement: 'optional',
        description: 'Policies, applications, or other context.',
        filePrefix: '[Supporting Document] '
      }
    ],
    instructions: NOTES
  },
  {
    id: CaraWorkspaceUseCase.ResearchClaimContext,
    preset: CaraWorkspacePreset.LossRunAnalysis,
    title: 'Research claim context',
    description: 'The research surfaces what the public record shows that the loss runs left off.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['claims'],
    moments: ['renewal', 'claims'],
    about: {
      summary:
        'Adds public context that may explain the loss history or reveal incidents missing from the loss runs. Each disclosed claim is checked against sources such as OSHA records, news reports, court filings and licensing data. The report separates favorable and adverse findings, cites every source and flags relevant public records not reflected in the carrier reports.',
      examples: [
        'A restaurant burn claim checked against news and health inspections before marketing',
        'A fleet with three at-fault accidents and no DOT or litigation record pulled yet',
        'A manufacturer leaving its incumbent, screened for enforcement actions and suits'
      ]
    },
    icon: GlobeAltIcon,
    pro: true,
    sources: [
      {
        label: 'Loss runs with disclosed claims',
        description: 'The claims Cara should research externally.',
        filePrefix: '[Loss Run] '
      },
      {
        label: 'Business and operations documents',
        requirement: 'optional',
        description: 'What the insured does, to focus the research.',
        filePrefix: '[Supporting Document] '
      }
    ],
    instructions: {
      label: 'Claims to prioritize',
      placeholder: 'Optional. specific claims, dates, or lines to focus on'
    }
  },
  {
    id: CaraWorkspaceUseCase.NegotiateRenewalRate,
    preset: CaraWorkspacePreset.LossRunAnalysis,
    title: 'Negotiate a renewal rate',
    description:
      'Creates an underwriter-ready summary that explains the loss history and supports the renewal request.',
    category: 'produce',
    lines: ['commercial'],
    tasks: ['clientDocuments', 'spreadsheets'],
    moments: ['renewal'],
    about: {
      summary:
        'Creates a concise statement to support the agency’s renewal request with the underwriter. It explains each significant loss, identifies documented controls that reduce the chance of recurrence and compares the history with the expiring policy and renewal offer. The final statement is 200 to 400 words and closes with the requested outcome.',
      examples: [
        'A twenty-two percent increase on a habitational renewal that has run clean three years',
        'A workers comp shock loss the underwriter is pricing like a pattern',
        'A cover memo framing 2023 auto losses for a contractor moving to a new market'
      ]
    },
    icon: CurrencyDollarIcon,
    pro: true,
    sources: [
      {
        label: 'Loss runs',
        description: 'Every loss run on file for the account.',
        filePrefix: '[Loss Run] '
      },
      {
        label: 'Current policy and renewal',
        description: 'The expiring policy and the renewal offer.',
        filePrefix: '[Supporting Document] '
      }
    ],
    instructions: {
      label: 'Negotiation goals',
      placeholder: 'Optional. renewal date, incumbent carrier, target outcome'
    }
  },

  // ── Risk Report (Pro) ─────────────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.GenerateRiskReport,
    preset: CaraWorkspacePreset.RiskReport,
    title: 'Generate a risk report',
    description:
      'Gives the business owner a clear view of major risks and up to ten priorities for reducing them.',
    category: 'produce',
    lines: ['personal', 'commercial'],
    tasks: ['clientDocuments'],
    moments: ['clientMeetings'],
    about: {
      summary:
        'Gives the business owner a clear, client-ready view of the company’s major risks and priorities. The PDF combines the uploaded policies and account documents with research on the business and its industry. It covers common claim costs, compliance obligations, litigation history and up to ten ranked recommendations.',
      examples: [
        'A stewardship meeting with a large contractor account and nothing to leave behind',
        'A manufacturing renewal where the owner should see the OSHA record before limits rise',
        'A new account where the report doubles as the first-year service plan'
      ]
    },
    icon: DocumentChartBarIcon,
    pro: true,
    sources: [
      {
        label: 'Policy and other documents',
        description:
          'The policies, applications and loss history the report should be grounded in.',
        filePrefix: '[Document] '
      }
    ],
    instructions: NOTES,
    deliverable: RISK_REPORT_DELIVERABLE,
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.ReviewIndustryTrends,
    preset: CaraWorkspacePreset.RiskReport,
    title: 'Review industry trends',
    description:
      'The report covers what is rising in frequency, severity and regulation, and needs no documents.',
    category: 'analyze',
    lines: ['commercial'],
    tasks: ['clientDocuments'],
    moments: ['clientMeetings'],
    about: {
      summary:
        'Provides a client-ready overview of the trends affecting an industry in a specific state. The PDF covers claim types increasing in frequency or cost, recent and expected regulatory changes, litigation trends and issues receiving more carrier attention. Because the analysis uses only the industry and location, its findings apply to the class as a whole and are not conclusions about a particular business.',
      examples: [
        'A newsletter piece on what is changing for HVAC contractors in one state',
        'Prospecting prep for a barely written class, like cannabis retail',
        'A trucking client asking what drives their premium up every year'
      ]
    },
    icon: ArrowTrendingUpIcon,
    pro: true,
    sources: [],
    instructions: {
      label: 'Industry and focus',
      placeholder: 'The industry, and any trends to cover'
    },
    deliverable: RISK_REPORT_DELIVERABLE,
    // An industry name on its own reads as the subject of a full report rather
    // than a request for the trends-only one, so the prompt keeps producing the
    // company-specific sections this card has no documents to fill.
    bodyPrefix: 'Review Industry Trends',
    requires: 'instructions'
  },
  {
    id: CaraWorkspaceUseCase.IdentifyRiskMitigations,
    preset: CaraWorkspacePreset.RiskReport,
    title: 'Identify risk mitigations',
    description:
      'Ranks practical risk improvements by importance, effort and timing, with a clear first step for each.',
    category: 'produce',
    lines: ['personal', 'commercial'],
    tasks: ['clientDocuments'],
    moments: ['clientMeetings'],
    about: {
      summary:
        'Provides a practical, client-ready plan for reducing the business’s most important risks. The PDF combines the attached policies and account documents with public information about the business. Each recommendation includes its priority, expected effort, timing and a clear first step.',
      examples: [
        'A client fresh off a failed fire marshal inspection who wants a punch list',
        'A roofer whose workers comp mod keeps drifting up ahead of renewal',
        'A dental practice where the list is the way into cyber and employment practices'
      ]
    },
    icon: CheckBadgeIcon,
    pro: true,
    sources: [
      {
        label: 'Policy and other documents',
        description: 'What the recommendations should be grounded in.',
        filePrefix: '[Document] '
      }
    ],
    instructions: NOTES,
    bodyPrefix: 'Identify Risk Mitigations',
    deliverable: RISK_REPORT_DELIVERABLE,
    requires: 'sources'
  },

  // ── Benefits Quotes (Pro) ─────────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.NormalizeQuotes,
    preset: CaraWorkspacePreset.EbQuoteAnalysis,
    title: 'Normalize benefits quotes',
    description:
      'Shows which benefits option costs less after pricing every quote for the same employees.',
    category: 'analyze',
    lines: ['benefits'],
    // Marketing a benefits plan is the same errand as shopping a risk, so the
    // reader who answers that question is asking for this family.
    tasks: ['findMarket', 'spreadsheets', 'checkDocuments'],
    moments: ['renewal', 'newBusiness'],
    about: {
      summary:
        'Shows which benefits option offers the best price for the same group of employees. Every quote is recalculated using one census, even when carriers use different rating methods, and each carrier’s stated total is checked against its own rates. A quote that does not add up is identified for follow-up and left out of the ranking.',
      examples: [
        'Four medical quotes, two age-banded and two composite, on one 80-life census',
        'A quote whose stated total is $3,100 off what its own rates produce',
        'Dental and vision quoted on different tier structures than the medical'
      ]
    },
    icon: ScaleIcon,
    pro: true,
    sources: [
      {
        label: 'Comparison census',
        description:
          'One census for the whole comparison. Include dates of birth and ZIP codes for age-banded rates.',
        filePrefix: '[Census] '
      },
      {
        label: 'Current plan and renewal',
        requirement: 'optional',
        description:
          'The current plan and incumbent renewal offer used to compare the alternatives.',
        filePrefix: '[Incumbent] ',
        suppliedMessageKey: 'Incumbent Supplied'
      },
      {
        label: 'Alternative quote and rates',
        description:
          'The proposal and rate sheet for one quote. Add each carrier as a separate option so its documents stay together in the comparison.',
        filePrefix: '[Option ',
        repeatable: true
      }
    ],
    instructions: NOTES,
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.BuildMarketingGrid,
    preset: CaraWorkspacePreset.EbQuoteAnalysis,
    title: 'Build a benefits marketing grid',
    description:
      'Creates an internal workbook that compares every quoted plan on price, benefits and open questions.',
    category: 'produce',
    lines: ['benefits'],
    tasks: ['findMarket', 'spreadsheets'],
    moments: ['renewal', 'newBusiness'],
    about: {
      summary:
        'Creates one internal workbook for reviewing all benefits quotes. It compares each option on price, plan design, funding terms and carrier conditions using the same employee census. Missing or conflicting information is listed for follow-up rather than filled in or overlooked.',
      examples: [
        'Six carriers back on a 200-life medical marketing, one grid for the file',
        'Two quotes contingent on participation nobody has confirmed yet',
        'A level-funded option next to fully insured, with the funding terms called out'
      ]
    },
    icon: TableCellsIcon,
    pro: true,
    sources: [
      {
        label: 'Comparison census',
        description:
          'One census for the whole comparison. Include dates of birth and ZIP codes for age-banded rates.',
        filePrefix: '[Census] '
      },
      {
        label: 'Current plan and renewal',
        requirement: 'optional',
        description:
          'The current plan and incumbent renewal offer used to compare the alternatives.',
        filePrefix: '[Incumbent] ',
        suppliedMessageKey: 'Incumbent Supplied'
      },
      {
        label: 'Current contributions',
        requirement: 'optional',
        description:
          'The employer’s current contribution by coverage tier. This shows employee costs if the current contribution remains unchanged.',
        filePrefix: '[Contributions] ',
        suppliedMessageKey: 'Contributions Supplied'
      },
      {
        label: 'Alternative quote and rates',
        description:
          'The proposal and rate sheet for one quote. Add each carrier as a separate option so its documents stay together in the comparison.',
        filePrefix: '[Option ',
        repeatable: true
      }
    ],
    instructions: NOTES,
    // The grid is the workbook, so the format is not a choice the reader makes.
    messageLines: [{ key: 'Output Format', value: 'Excel', position: 'trailing' }],
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.BuildFinalistComparison,
    preset: CaraWorkspacePreset.EbQuoteAnalysis,
    title: 'Compare benefits finalists',
    description:
      'Creates a client-ready comparison of the finalist plans whose pricing has been verified.',
    category: 'produce',
    lines: ['benefits'],
    tasks: ['findMarket', 'clientDocuments', 'spreadsheets'],
    moments: ['renewal', 'newBusiness', 'clientMeetings'],
    about: {
      summary:
        'Creates a client-ready comparison of the strongest benefits options. It shows the cost, plan differences and employee payroll impact of each finalist using the current employer contribution. Any quote whose pricing cannot be verified is left out of the comparison and identified separately.',
      examples: [
        'Two finalists for the committee after four carriers were marketed',
        'A renewal held against one alternative for the employer meeting',
        'A finalist set where the third option was dropped for unreconciled rates'
      ]
    },
    icon: PresentationChartBarIcon,
    pro: true,
    sources: [
      {
        label: 'Comparison census',
        description:
          'One census for the whole comparison. Include dates of birth and ZIP codes for age-banded rates.',
        filePrefix: '[Census] '
      },
      {
        label: 'Current plan and renewal',
        requirement: 'optional',
        description: 'The current plan and incumbent renewal offer used to compare the finalists.',
        filePrefix: '[Incumbent] ',
        suppliedMessageKey: 'Incumbent Supplied'
      },
      {
        label: 'Current contributions',
        requirement: 'optional',
        description:
          'The employer’s current contribution by coverage tier. This shows employee costs if the current contribution remains unchanged.',
        filePrefix: '[Contributions] ',
        suppliedMessageKey: 'Contributions Supplied'
      },
      {
        label: 'Finalist quote and rates',
        description:
          'The proposal and rate sheet for one finalist. Add each carrier as a separate option so its documents stay together in the comparison.',
        filePrefix: '[Option ',
        repeatable: true
      }
    ],
    instructions: NOTES,
    deliverable: benefitsWorkbook('workspace'),
    requires: 'sources'
  },

  // ── Benefits Contributions (Pro) ──────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.CreateAnalysis,
    preset: CaraWorkspacePreset.EbContributionAnalysis,
    title: 'Analyze contributions at renewal',
    description:
      'Shows how the renewal and other plan options change employer costs and employee payroll deductions.',
    category: 'analyze',
    lines: ['benefits'],
    tasks: ['spreadsheets'],
    moments: ['renewal', 'clientMeetings'],
    about: {
      summary:
        'Shows how each renewal option affects the employer’s total cost and employee payroll deductions. Every plan is priced for the same enrolled employees and divided using the current contribution structure. The analysis also tests ACA affordability, using available employee pay data or the federal poverty line method when pay data is not provided.',
      examples: [
        'A 14 percent medical renewal, and what holding the split costs the employer',
        'A renewal plus two buy-down options measured against today',
        'An employer near the affordability line on its lowest-cost self-only option'
      ]
    },
    icon: ScaleIcon,
    pro: true,
    sources: [
      {
        label: 'Census and enrollment',
        description:
          'Who is enrolled in each plan, coverage tier and employee class. Include pay data to test ACA affordability using the rate-of-pay and W-2 methods.',
        filePrefix: '[Census] '
      },
      {
        label: 'Current plan and rates',
        description: 'Today’s rates and today’s contribution structure.',
        filePrefix: '[Current Rates] '
      },
      {
        label: 'Renewal',
        requirement: 'optional',
        description: 'The carrier’s renewal rates for the coming plan year.',
        filePrefix: '[Renewal] ',
        suppliedMessageKey: 'Renewal Supplied'
      },
      {
        label: 'Quoted alternatives',
        requirement: 'optional',
        description: 'Other options to price on the same census.',
        filePrefix: '[Plan Option] ',
        suppliedMessageKey: 'Plan Options Supplied'
      },
      {
        label: 'Current carrier invoice',
        requirement: 'optional',
        description:
          'The latest bill, used to confirm current enrollment and cost before modeling.',
        filePrefix: '[Invoice] '
      }
    ],
    instructions: NOTES,
    deliverable: benefitsWorkbook('workspace'),
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.BuildContributionWorksheet,
    preset: CaraWorkspacePreset.EbContributionAnalysis,
    title: 'Build a contribution worksheet',
    description: 'Compares several ways to divide plan costs between the employer and employees.',
    category: 'produce',
    lines: ['benefits'],
    tasks: ['spreadsheets'],
    moments: ['renewal', 'clientMeetings'],
    about: {
      summary:
        'Compares several employer contribution strategies side by side, including a percentage of premium, a fixed dollar amount and a defined contribution. For each strategy, the worksheet shows total employer cost and employee cost by plan and coverage tier. It also identifies options that may not meet affordability rules or carrier contribution minimums.',
      examples: [
        'Sold rates that need an employer and employee split before enrollment',
        'Three structures priced for an employer who has not decided',
        'A move from percentage-of-premium to a flat dollar contribution'
      ]
    },
    icon: CalculatorIcon,
    pro: true,
    sources: [
      {
        label: 'Census and enrollment',
        description:
          'Who is enrolled in each plan, coverage tier and employee class. Include pay data to test ACA affordability using the rate-of-pay and W-2 methods.',
        filePrefix: '[Census] '
      },
      {
        label: 'Plan rates',
        description: 'The carrier rate sheets the structures are priced on, by plan and tier.',
        filePrefix: '[Current Rates] '
      },
      {
        label: 'Quoted alternatives',
        requirement: 'optional',
        description: 'Other options to price on the same census.',
        filePrefix: '[Plan Option] ',
        suppliedMessageKey: 'Plan Options Supplied'
      }
    ],
    instructions: NOTES,
    deliverable: benefitsWorkbook('xlsx'),
    // This run prices structures onto the rates rather than measuring a change,
    // so it offers no renewal lane and has to say so: an unanswered flag reads
    // as a renewal that was supplied.
    messageLines: [{ key: 'Renewal Supplied', value: 'no', position: 'trailing' }],
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.SolveToBudget,
    preset: CaraWorkspacePreset.EbContributionAnalysis,
    title: 'Solve to a benefits budget',
    description:
      'Finds the employer contribution strategy that comes closest to the benefits budget.',
    category: 'analyze',
    lines: ['benefits'],
    tasks: ['spreadsheets'],
    moments: ['renewal', 'clientMeetings'],
    about: {
      summary:
        'Finds a contribution strategy that meets the employer’s benefits budget while showing the resulting employee costs. Each option is priced using the employee census and available plan rates. If the target cannot be met, the analysis explains why and shows the closest workable result.',
      examples: [
        'An employer who will fund a 4 percent increase against a 14 percent renewal',
        'A hard dollar cap on next year’s benefits spend',
        'Holding spend flat, and what that does to the family tier'
      ]
    },
    icon: BanknotesIcon,
    pro: true,
    sources: [
      {
        label: 'Census and enrollment',
        description:
          'Who is enrolled in each plan, coverage tier and employee class. Include pay data to test ACA affordability using the rate-of-pay and W-2 methods.',
        filePrefix: '[Census] '
      },
      {
        label: 'Current plan and rates',
        description: 'Today’s rates and today’s contribution structure.',
        filePrefix: '[Current Rates] '
      },
      {
        label: 'Renewal',
        requirement: 'optional',
        description: 'The renewal offer the target is measured against.',
        filePrefix: '[Renewal] ',
        suppliedMessageKey: 'Renewal Supplied'
      },
      {
        label: 'Quoted alternatives',
        requirement: 'optional',
        description: 'Other options the solve may reach the target with.',
        filePrefix: '[Plan Option] ',
        suppliedMessageKey: 'Plan Options Supplied'
      }
    ],
    fields: [
      {
        // Without this line the prompt has nothing to solve toward and reports the
        // options as priced instead.
        key: 'budgetTarget',
        label: 'Budget target',
        description:
          'What the employer will accept: a percentage, a dollar figure, or “hold our spend flat”.',
        messageKey: 'Employer Budget Target'
      }
    ],
    instructions: NOTES,
    deliverable: benefitsWorkbook('workspace'),
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.BuildDeductionSchedule,
    preset: CaraWorkspacePreset.EbContributionAnalysis,
    title: 'Build a deduction schedule',
    description:
      'Creates the employee payroll deductions for each plan, coverage tier, class and pay period.',
    category: 'produce',
    lines: ['benefits'],
    tasks: ['spreadsheets'],
    moments: ['renewal', 'service'],
    about: {
      summary:
        'Provides payroll with the amount to deduct from each paycheck by plan, coverage tier and employee class. Monthly contributions are converted using the stated payroll frequency or the frequency shown in the source documents. If no reliable payroll schedule is available, the worksheet reports monthly amounts instead of estimating deductions.',
      examples: [
        'Sold rates and a chosen split that payroll needs before the first deduction',
        'A biweekly employer where dividing the monthly rate by two is wrong',
        'Two classes on different contribution structures in one schedule'
      ]
    },
    icon: TableCellsIcon,
    pro: true,
    sources: [
      {
        label: 'Census and enrollment',
        description: 'Who is enrolled, in what plan, tier and class.',
        filePrefix: '[Census] '
      },
      {
        // One bucket takes both plan years; the effective dates inside say which is which.
        label: 'Plan rates and contributions',
        description: 'The rates and the contribution structure to convert.',
        filePrefix: '[Plan Rates] '
      }
    ],
    fields: [
      {
        key: 'payrollFrequency',
        label: 'Payroll frequency',
        description: 'Pay periods per year. Left unset, it comes from the documents.',
        messageKey: 'Payroll Frequency',
        choices: [
          { value: '', label: 'Take it from the documents' },
          { value: '26', label: 'Biweekly (26)' },
          { value: '24', label: 'Semi-monthly (24)' },
          { value: '12', label: 'Monthly (12)' },
          { value: '52', label: 'Weekly (52)' }
        ]
      }
    ],
    instructions: NOTES,
    deliverable: benefitsWorkbook('workspace'),
    requires: 'sources'
  },

  // ── Benefits Census (Pro) ─────────────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.FormatForPlatform,
    preset: CaraWorkspacePreset.EbCensusNormalization,
    title: 'Format a census for a platform',
    description:
      'Cara rewrites the roster into the destination platform’s exact headers and lists what it could not place.',
    category: 'produce',
    lines: ['benefits'],
    tasks: ['spreadsheets'],
    moments: ['renewal', 'newBusiness'],
    about: {
      summary:
        'Produces a census file in the exact layout required by a carrier or benefits platform. It maps the source roster to the destination headers without adding unsupported information. Any row or field that cannot be placed is listed for review instead of being silently left blank.',
      examples: [
        'An HR export onto a benefits administration platform’s import sheet',
        'A carrier RFP template that wants tiers this census does not use',
        'Three location files consolidated onto one enrollment layout'
      ]
    },
    icon: TableCellsIcon,
    pro: true,
    sources: [
      {
        label: 'Census',
        description: 'The roster to reformat. CSV or Excel.',
        filePrefix: '[Census] '
      },
      {
        label: 'Destination layout',
        description:
          'A blank copy of the destination file. Its headers are matched exactly, which is what makes the output loadable.',
        filePrefix: '[Destination Layout] '
      },
      {
        label: 'Field requirements',
        requirement: 'optional',
        description: 'Written rules for the destination: required fields and formats.',
        filePrefix: '[Field Requirements] ',
        suppliedMessageKey: 'Requirements Supplied'
      },
      {
        label: 'Prior census',
        requirement: 'optional',
        description: 'Last renewal’s roster, for the adds, removals and changes since.',
        filePrefix: '[Prior Census] ',
        suppliedMessageKey: 'Prior Census Supplied'
      }
    ],
    instructions: NOTES,
    deliverable: {
      label: 'Layout',
      description: 'Choose the destination layout for the completed census.',
      choices: [
        {
          key: 'attached',
          label: 'The attached layout',
          icon: DocumentTextIcon,
          requiresLane: '[Destination Layout] ',
          description: 'Its headers are matched exactly and nothing is added.'
        },
        {
          key: 'template',
          label: 'A saved layout',
          icon: TableCellsIcon,
          template: true,
          kinds: ['excel'],
          description: 'The layout comes from the agency library.'
        }
      ],
      defaultKey: 'attached'
    },
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.CleanAndValidate,
    preset: CaraWorkspacePreset.EbCensusNormalization,
    title: 'Clean and validate a census',
    description:
      'Returns a consistent employee census and identifies every row that still needs manual review.',
    category: 'verify',
    lines: ['benefits'],
    tasks: ['spreadsheets', 'checkDocuments'],
    moments: ['renewal', 'newBusiness'],
    about: {
      summary:
        'Produces a clean, consistent census and a list of records that still need attention. It standardizes names, dates, coverage tiers and family relationships, then checks for issues such as duplicates, unreadable dates, dependents without a subscriber and tiers that conflict with household details. Every issue points back to the affected row.',
      examples: [
        'A census going to market where three tiers contradict the dependent counts',
        'An HR export with dates in two formats and four duplicate employees',
        'A roster where two departments used different names for the same tier'
      ]
    },
    icon: DocumentCheckIcon,
    pro: true,
    sources: [
      {
        label: 'Census',
        description: 'The roster to normalize. CSV or Excel.',
        filePrefix: '[Census] '
      },
      {
        label: 'Field requirements',
        requirement: 'optional',
        description:
          'Written rules the census has to satisfy. Without them the run applies standard formatting checks only.',
        filePrefix: '[Field Requirements] ',
        suppliedMessageKey: 'Requirements Supplied'
      },
      {
        label: 'Prior census',
        requirement: 'optional',
        description: 'Last renewal’s roster, for the adds, removals and changes since.',
        filePrefix: '[Prior Census] ',
        suppliedMessageKey: 'Prior Census Supplied'
      }
    ],
    instructions: NOTES,
    requires: 'sources'
  },

  // ── Benefits Billing Audit (Pro) ──────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.ReconcileFirstInvoice,
    preset: CaraWorkspacePreset.EbBillingAudit,
    // One tile audits both bills. A first bill changes the evidence worth asking
    // for and how much weight the proration rule carries, never which checks
    // run, so the prompt reads it off whether a prior bill came with the upload.
    unavailable: 'Audit a benefits invoice covers the first bill too.',
    supersededBy: CaraWorkspaceUseCase.ReconcileMonthlyInvoice,
    title: 'Audit the first benefits invoice',
    description:
      'The audit checks the first bill after implementation against what was enrolled and what was sold.',
    category: 'verify',
    lines: ['benefits'],
    tasks: ['spreadsheets', 'checkDocuments'],
    moments: ['service', 'newBusiness'],
    about: {
      summary:
        'Shows whether the first invoice matches enrollment and the agreed rates and explains any difference. The audit first confirms that the invoice adds up, then calculates the expected premium from the enrollment roster and rates. A detailed invoice can be checked employee by employee, while a summary invoice supports only enrollment counts and totals by coverage tier.',
      examples: [
        'A first bill $9,400 over expected because one class loaded at the wrong tier',
        'An implementation where eight waivers were installed as enrolled',
        'A bill charging composite rates on a case that was sold age-banded'
      ]
    },
    icon: ReceiptPercentIcon,
    pro: true,
    sources: [
      {
        label: 'Carrier invoice',
        description:
          'The bill being audited. Subscriber-level detail supports findings by employee; a summary bill can only be checked by enrollment count and coverage tier.',
        filePrefix: '[Carrier Invoice] '
      },
      {
        label: 'Enrollment roster',
        description: 'Who should be covered, in what plan and tier.',
        filePrefix: '[Enrollment Roster] '
      },
      {
        label: 'Bound rates',
        requirement: 'optional',
        description:
          'The rates agreed at binding. Without them, the audit cannot calculate the expected premium or the amount of a carrier correction.',
        filePrefix: '[Bound Rates] ',
        suppliedMessageKey: 'Rates Supplied'
      },
      {
        label: 'Submitted changes',
        requirement: 'optional',
        description:
          'Dated enrollment forms and carrier confirmations used to distinguish a carrier error from a change submitted too late for the billing period.',
        filePrefix: '[Submitted Changes] ',
        suppliedMessageKey: 'Change Evidence Supplied'
      },
      {
        label: 'Contract and billing rules',
        requirement: 'optional',
        description:
          'Documents showing partial-month charges, deadlines for retroactive changes and payment due dates. The audit applies only terms supported by these documents.',
        filePrefix: '[Contract and Billing Rules] ',
        suppliedMessageKey: 'Contract Supplied'
      }
    ],
    instructions: NOTES,
    deliverable: benefitsWorkbook('workspace'),
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.ReconcileMonthlyInvoice,
    preset: CaraWorkspacePreset.EbBillingAudit,
    title: 'Audit a benefits invoice',
    description:
      'The audit finds who is billed but not enrolled, enrolled but not billed, and billed at the wrong rate.',
    category: 'verify',
    lines: ['benefits'],
    tasks: ['spreadsheets', 'checkDocuments'],
    moments: ['service', 'newBusiness'],
    about: {
      summary:
        'Explains why the carrier invoice differs from the enrollment roster and identifies the corrections or credits to request. When bound rates are provided, the audit calculates the expected premium and lists employees who are missing, still billed after termination, enrolled in the wrong plan or charged the wrong rate. A summary invoice can be checked by enrollment count and coverage tier, but identifying individual errors requires a bill with subscriber-level detail. Adding the prior invoice also shows what changed and whether an adjustment was applied twice.',
      examples: [
        'Four terminated employees still billed two months after their last day',
        'A retroactive credit that already appeared on last month’s bill',
        'The first bill after implementation, loaded at the quoted rate instead of the sold rate'
      ]
    },
    icon: ReceiptPercentIcon,
    pro: true,
    sources: [
      {
        label: 'Carrier invoice',
        description:
          'The bill being audited. Subscriber-level detail supports findings by employee; a summary bill can only be checked by enrollment count and coverage tier.',
        filePrefix: '[Carrier Invoice] '
      },
      {
        label: 'Enrollment roster',
        description: 'Who should be covered, in what plan and tier.',
        filePrefix: '[Enrollment Roster] '
      },
      {
        label: 'Bound rates',
        requirement: 'optional',
        description:
          'The rates agreed at binding. Without them, the audit cannot calculate the expected premium or the amount of a carrier correction.',
        filePrefix: '[Bound Rates] ',
        suppliedMessageKey: 'Rates Supplied'
      },
      {
        label: 'Submitted changes',
        requirement: 'optional',
        description:
          'Dated enrollment forms and carrier confirmations used to distinguish a carrier error from a change submitted too late for the billing period.',
        filePrefix: '[Submitted Changes] ',
        suppliedMessageKey: 'Change Evidence Supplied'
      },
      {
        label: 'Contract and billing rules',
        requirement: 'optional',
        description:
          'Documents showing partial-month charges, deadlines for retroactive changes and payment due dates. The audit applies only terms supported by these documents.',
        filePrefix: '[Contract and Billing Rules] ',
        suppliedMessageKey: 'Contract Supplied'
      },
      {
        label: 'Prior invoice',
        requirement: 'optional',
        description:
          'The previous bill, used to show what changed and whether a credit or charge was applied twice. If omitted, the invoice is treated as the first bill after implementation.',
        filePrefix: '[Prior Invoice] ',
        suppliedMessageKey: 'Prior Invoice Supplied'
      }
    ],
    instructions: NOTES,
    deliverable: benefitsWorkbook('workspace'),
    requires: 'sources'
  },

  // ── Benefits Claims Review (Pro) ──────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.QuarterlyReview,
    preset: CaraWorkspacePreset.EbClaimsReview,
    title: 'Review medical claims experience',
    description:
      'Shows whether medical and pharmacy claims costs are rising and identifies what is driving the change.',
    category: 'analyze',
    lines: ['benefits'],
    tasks: ['claims', 'spreadsheets'],
    moments: ['clientMeetings', 'service', 'renewal'],
    about: {
      summary:
        'Shows how the group’s medical and pharmacy claims costs are changing and which large claims account for most of the spending. The trend uses only periods with sufficiently complete claims payments, so recent months with claims still being processed do not distort the result. If large-claim and stop-loss reports are provided, the review also shows each claimant’s progress toward the individual stop-loss deductible and the group’s progress toward its overall stop-loss threshold. Claimants are identified by aliases rather than names.',
      examples: [
        'A self-funded group’s stewardship review two quarters into the year',
        'A level-funded case where pharmacy is growing faster than medical',
        'A 300-life group where four claimants are 38 percent of paid claims'
      ]
    },
    icon: ChartBarIcon,
    pro: true,
    sources: [
      {
        label: 'Claims experience',
        description:
          'The carrier or TPA experience report: paid claims by month, medical and pharmacy. Add monthly enrollment counts and funding rates if they came separately.',
        filePrefix: '[Experience] '
      },
      {
        label: 'Large claimant report',
        requirement: 'optional',
        description:
          'Claimants above the reporting threshold, including diagnosis category and claims paid to date. This report is required to measure how much spending is concentrated in large claims. Names are excluded from the output.',
        filePrefix: '[Large Claimants] ',
        suppliedMessageKey: 'Large Claimants Supplied'
      },
      {
        label: 'Stop-loss contract',
        requirement: 'optional',
        description:
          'The individual and group stop-loss thresholds and any claimant-specific limits. Without it, the review cannot show how close claims are to reimbursement thresholds.',
        filePrefix: '[Stop-Loss] ',
        suppliedMessageKey: 'Stop-Loss Supplied'
      }
    ],
    instructions: NOTES,
    options: [
      {
        key: 'renewalPositioning',
        label: 'Frame for the renewal conversation',
        description:
          'Adds what the experience supports at the renewal table and what only the carrier can answer.',
        defaultOn: false,
        messageKey: 'Renewal Positioning'
      }
    ],
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.PreRenewalReview,
    preset: CaraWorkspacePreset.EbClaimsReview,
    // The renewal framing added one section to an otherwise identical run, so it
    // is a switch on the review rather than a second tile.
    unavailable: 'Review medical claims experience carries the renewal framing as an option.',
    supersededBy: CaraWorkspaceUseCase.QuarterlyReview,
    title: 'Prepare claims experience for renewal',
    description:
      'The review separates what the experience will support at the renewal table from what it will not.',
    category: 'analyze',
    lines: ['benefits'],
    tasks: ['claims', 'spreadsheets'],
    moments: ['renewal'],
    about: {
      summary:
        'Shows which claims findings can support the renewal discussion and which questions still require carrier answers. It explains how the carrier has treated each large claimant, identifies recent months with incomplete payments and flags projections that go beyond the available data. It does not predict future claims from a diagnosis alone and relies on the carrier’s documented assessment.',
      examples: [
        'A renewal quoted off a quarter that includes two resolved transplants',
        'Building the case that a 22 percent increase is not supported',
        'A group heading to market where the questions matter more than the numbers'
      ]
    },
    icon: ArrowTrendingUpIcon,
    pro: true,
    sources: [
      {
        label: 'Claims experience',
        description:
          'The carrier or TPA experience report: paid claims by month, medical and pharmacy. Add monthly enrollment counts and funding rates if they came separately.',
        filePrefix: '[Experience] '
      },
      {
        label: 'Large claimant report',
        requirement: 'optional',
        description:
          'Claimants above the reporting threshold, with diagnosis category and paid to date. Sets up what to ask the carrier about its treatment of each. No names appear in the output.',
        filePrefix: '[Large Claimants] ',
        suppliedMessageKey: 'Large Claimants Supplied'
      },
      {
        label: 'Stop-loss contract',
        requirement: 'optional',
        description:
          'The individual and group stop-loss thresholds and any claimant-specific limits. Without it, the review cannot show how close claims are to reimbursement thresholds.',
        filePrefix: '[Stop-Loss] ',
        suppliedMessageKey: 'Stop-Loss Supplied'
      }
    ],
    instructions: NOTES,
    requires: 'sources'
  },

  // ── Benefits Document Review (Pro) ────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.ReviewBenefitsGuide,
    preset: CaraWorkspacePreset.EbDocumentReview,
    title: 'Review a benefits guide',
    description:
      'The review checks the enrollment guide against the rate sheets and plan documents behind it.',
    category: 'verify',
    lines: ['benefits'],
    tasks: ['checkDocuments'],
    moments: ['renewal', 'service'],
    about: {
      summary:
        'Identifies incorrect or unsupported information in a benefits guide before employees receive it. Every rate, benefit and payroll deduction is checked against the carrier rate sheets, plan documents and contribution worksheet. Each finding names the affected page and the source document that supports the correction.',
      examples: [
        'An open enrollment packet whose dental rates came from last year’s sheet',
        'A guide showing a $2,000 deductible the plan document puts at $2,500',
        'Per-paycheck costs built on a monthly figure divided by the wrong number'
      ]
    },
    icon: ClipboardDocumentCheckIcon,
    pro: true,
    sources: [
      {
        label: 'Guide under review',
        description: 'The benefits guide or enrollment packet being checked.',
        filePrefix: '[Under Review] '
      },
      {
        label: 'Carrier rate sheets',
        description:
          'The rate exhibits the guide should match, for this plan year. Add one per carrier.',
        filePrefix: '[Rate Sheet] '
      },
      {
        label: 'Supporting documents',
        requirement: 'optional',
        description: 'Plan documents, benefit summaries and the contribution worksheet.',
        filePrefix: '[Support] '
      }
    ],
    instructions: NOTES,
    deliverable: benefitsWorkbook('workspace'),
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.VerifyContributions,
    preset: CaraWorkspacePreset.EbDocumentReview,
    title: 'Verify a contribution worksheet',
    description: 'The review checks each figure in the worksheet against the carrier rate sheets.',
    category: 'verify',
    lines: ['benefits'],
    tasks: ['checkDocuments', 'spreadsheets'],
    moments: ['renewal', 'service'],
    about: {
      summary:
        'Identifies incorrect rates, contribution splits and payroll deductions in a contribution worksheet. Each plan is checked against the correct carrier rate sheet, and the employer and employee portions must add back to the full premium. The review also confirms that per-paycheck amounts use the correct number of pay periods.',
      examples: [
        'A worksheet whose vision rates were never updated at renewal',
        'A split where employer and employee do not sum to the premium',
        'Biweekly deductions built by dividing the monthly rate by two'
      ]
    },
    icon: CalculatorIcon,
    pro: true,
    sources: [
      {
        label: 'Worksheet under review',
        description: 'The contributions worksheet being checked.',
        filePrefix: '[Under Review] '
      },
      {
        label: 'Carrier rate sheets',
        description:
          'The rate exhibits the worksheet should match, for this plan year. Add one per carrier.',
        filePrefix: '[Rate Sheet] '
      },
      {
        label: 'Supporting documents',
        requirement: 'optional',
        description: 'Census, plan documents, or the payroll calendar.',
        filePrefix: '[Support] '
      }
    ],
    instructions: NOTES,
    deliverable: benefitsWorkbook('workspace'),
    requires: 'sources'
  },

  // ── Open Enrollment Materials (Pro) ───────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.BuildEnrollmentForm,
    preset: CaraWorkspacePreset.EbEnrollmentMaterials,
    title: 'Build the enrollment election form',
    description:
      'The form gives employees one line per coverage to elect or decline, with the cost per paycheck.',
    category: 'produce',
    lines: ['benefits'],
    tasks: ['clientDocuments', 'fillForms'],
    moments: ['renewal', 'service'],
    about: {
      summary:
        'Creates an enrollment form employees can use to elect or decline each available benefit. The form shows plan choices, coverage tiers and cost per paycheck, with authorization and signature sections. If the employer’s approved authorization language is not provided, that section is left clearly marked for completion.',
      examples: [
        'A 40-life employer with no benefits administration platform',
        'A paper form for a location where employees have no company email',
        'A new dental line that needs an affirmative election or waiver on file'
      ]
    },
    icon: ClipboardDocumentListIcon,
    pro: true,
    sources: [
      {
        label: 'This year’s documents',
        description:
          'The bound rates, the benefit summaries for every line, and the contribution worksheet with the employer and employee split. Include the per-paycheck column if it has one.',
        filePrefix: '[This Year] '
      },
      {
        label: 'Last year’s material',
        requirement: 'optional',
        description:
          'The prior form. Adds the what-changed section, and usually states how often employees are paid.',
        filePrefix: '[Last Year] ',
        suppliedMessageKey: 'Prior Material Supplied'
      }
    ],
    instructions: NOTES,
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.BuildBenefitsGuide,
    preset: CaraWorkspacePreset.EbEnrollmentMaterials,
    title: 'Build the employee benefits guide',
    description:
      'Creates an employee guide with plan benefits, payroll costs, carrier contacts and required notices.',
    category: 'produce',
    lines: ['benefits'],
    tasks: ['clientDocuments'],
    moments: ['renewal', 'clientMeetings'],
    about: {
      summary:
        'Creates an employee benefits guide for open enrollment using the attached plan and contribution documents. It includes an at-a-glance summary, one section for each benefit, plan comparisons, cost per paycheck and carrier contact information. Required notices are identified but not drafted, and every figure comes from an attached source.',
      examples: [
        'An employer adding vision and needing the guide rebuilt around it',
        'A guide for two classes on different contribution structures',
        'Replacing a carrier-branded booklet with one that carries agency branding'
      ]
    },
    icon: BookOpenIcon,
    pro: true,
    sources: [
      {
        label: 'This year’s documents',
        description:
          'The bound rates, the benefit summaries for every line, and the contribution worksheet with the employer and employee split. Include the per-paycheck column if it has one.',
        filePrefix: '[This Year] '
      },
      {
        label: 'Last year’s material',
        requirement: 'optional',
        description: 'The prior guide. Adds the what-changed section employees look for first.',
        filePrefix: '[Last Year] ',
        suppliedMessageKey: 'Prior Material Supplied'
      }
    ],
    instructions: NOTES,
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.BuildAnnouncement,
    preset: CaraWorkspacePreset.EbEnrollmentMaterials,
    title: 'Build the open enrollment announcement',
    description:
      'Creates an employee email with enrollment deadlines, required actions and answers to common questions.',
    category: 'produce',
    lines: ['benefits'],
    tasks: ['clientDocuments'],
    moments: ['renewal', 'service'],
    about: {
      summary:
        'Creates an employee announcement that clearly explains the open enrollment deadline, what is changing and what action is required. It also includes answers to common questions supported by the plan documents. Questions the documents cannot answer are listed for the employer or broker to complete.',
      examples: [
        'A passive enrollment where only the employees losing a plan must act',
        'A first announcement for an employer moving to active enrollment',
        'A deadline reminder after a week of no elections'
      ]
    },
    icon: MegaphoneIcon,
    pro: true,
    sources: [
      {
        label: 'This year’s documents',
        description:
          'The bound rates, the benefit summaries for every line, and the contribution worksheet with the employer and employee split.',
        filePrefix: '[This Year] '
      },
      {
        label: 'Last year’s material',
        requirement: 'optional',
        description: 'The prior announcement, for what changed and the house voice.',
        filePrefix: '[Last Year] ',
        suppliedMessageKey: 'Prior Material Supplied'
      }
    ],
    instructions: NOTES,
    requires: 'sources'
  },

  // ── Commission Statements (Pro) ───────────────────────────────────────────
  {
    id: CaraWorkspaceUseCase.ConvertStatement,
    preset: CaraWorkspacePreset.CommissionProcessing,
    title: 'Convert statements to a register',
    description:
      'The register consolidates any number of carriers and periods into one normalized table.',
    category: 'produce',
    // Commission arrives on the same statement whatever the agency writes, and
    // the run reads a layout rather than a line of business.
    lines: ['personal', 'commercial', 'benefits'],
    tasks: ['spreadsheets'],
    moments: ['service'],
    about: {
      summary:
        'Creates one commission register from statements supplied by different carriers and for different periods. Each statement is checked for complete extraction, correct totals, duplicate rows, adjustments and calculation differences before it is added. Any statement that cannot be verified is identified separately. This run standardizes what the statements report; it does not determine what the agency was owed or post entries to another system.',
      examples: [
        'Eleven carrier statements for one month, one register',
        'A PDF statement whose columns shift halfway down the page',
        'A quarter of statements loaded into an accounting import sheet'
      ]
    },
    icon: TableCellsIcon,
    pro: true,
    sources: [
      {
        label: 'Statements',
        description: 'The statements to process. PDF, Excel or CSV, any number of carriers.',
        filePrefix: '[Statement] '
      },
      {
        label: 'Destination layout',
        requirement: 'optional',
        description: 'A blank copy of the workbook to fill. Its column order is matched exactly.',
        filePrefix: '[Destination Layout] '
      }
    ],
    instructions: NOTES,
    deliverable: {
      label: 'Register',
      description: 'Choose where to receive the commission register.',
      choices: [
        {
          key: 'xlsx',
          label: 'Excel register',
          icon: TableCellsIcon,
          messageValue: 'Excel',
          description: 'Cara builds the register with source totals and exception tabs.'
        },
        {
          key: 'template',
          label: 'A saved layout',
          icon: DocumentTextIcon,
          messageValue: 'Excel',
          template: true,
          kinds: ['excel'],
          description:
            'Headers are matched exactly; anything the layout has no column for goes on its own tab.'
        },
        {
          key: 'workspace',
          label: 'Table in the workspace',
          icon: SparklesIcon,
          messageValue: 'text',
          description: 'A markdown table, ready to read and copy.'
        }
      ],
      defaultKey: 'xlsx',
      messageKey: 'Output Format',
      messagePosition: 'trailing'
    },
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.ReconcileStatement,
    preset: CaraWorkspacePreset.CommissionProcessing,
    title: 'Audit commission for errors',
    description:
      'Finds payments that do not match the commission schedule and active accounts missing from statements.',
    category: 'verify',
    lines: ['personal', 'commercial', 'benefits'],
    tasks: ['spreadsheets', 'checkDocuments'],
    moments: ['service'],
    about: {
      summary:
        'Identifies commission payments that are missing or do not match the agency’s agreed rates. When a commission schedule is provided, each payment is checked against the amount on which commission should be calculated and the agreed rate. A book-of-business report can also reveal active accounts that do not appear on any statement. The result is an exception list and a draft carrier inquiry, but each difference still requires confirmation and does not by itself prove that the carrier underpaid.',
      examples: [
        'A carrier paying 4 percent on a schedule that says 5',
        'Six in-force accounts absent from a quarter of statements',
        'A statement where the premium basis is right and the rate is not'
      ]
    },
    icon: ReceiptPercentIcon,
    pro: true,
    sources: [
      {
        label: 'Statements',
        description: 'The statements to check. PDF, Excel or CSV, any number of carriers.',
        filePrefix: '[Statement] '
      },
      {
        label: 'Commission schedule',
        requirement: 'optional',
        description:
          'The agreed rates by carrier, product and group. Without it a wrong rate cannot be caught, only wrong arithmetic.',
        filePrefix: '[Commission Schedule] ',
        suppliedMessageKey: 'Schedule Supplied'
      },
      {
        label: 'Book of business',
        requirement: 'optional',
        description:
          'A management system export of the accounts that should appear. An export naming policy term and status supports more checks than a plain list.',
        filePrefix: '[Book of Business] ',
        suppliedMessageKey: 'Book Of Business Supplied'
      }
    ],
    instructions: NOTES,
    deliverable: benefitsWorkbook('workspace'),
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.AnalyzeCommission,
    preset: CaraWorkspacePreset.CommissionProcessing,
    title: 'Explain how commission moved',
    description:
      'Explains why commission changed between periods, including rates, volume, accounts and adjustments.',
    category: 'analyze',
    lines: ['personal', 'commercial', 'benefits'],
    tasks: ['spreadsheets'],
    moments: ['service'],
    about: {
      summary:
        'Explains why commission revenue increased or decreased between two periods. The analysis separates changes caused by commission rates, premium volume, accounts gained or lost and one-time adjustments, with detail by carrier, account and producer. It is designed to explain the change, not verify whether every carrier paid the correct rate.',
      examples: [
        'Revenue down 8 percent, and how much of it is one adjustment',
        'A quarter-over-quarter read by producer before compensation review',
        'One carrier growing while two shrink inside a flat total'
      ]
    },
    icon: PresentationChartBarIcon,
    pro: true,
    sources: [
      {
        label: 'Statements',
        description: 'This period’s statements. PDF, Excel or CSV.',
        filePrefix: '[Statement] '
      },
      {
        label: 'Prior-period statements',
        description:
          'Last period’s statements for the same carriers. Both periods are required to explain what changed.',
        filePrefix: '[Prior Statement] ',
        suppliedMessageKey: 'Prior Period Supplied'
      }
    ],
    instructions: NOTES,
    deliverable: benefitsWorkbook('workspace'),
    requires: 'sources'
  },
  {
    id: CaraWorkspaceUseCase.ReconcileReceipts,
    preset: CaraWorkspacePreset.CommissionProcessing,
    title: 'Reconcile commission receipts',
    description:
      'Shows which carrier receipts agree across accounting and the management system and which need follow-up.',
    category: 'verify',
    lines: ['personal', 'commercial', 'benefits'],
    tasks: ['spreadsheets', 'checkDocuments'],
    moments: ['service'],
    about: {
      summary:
        'Shows which commission receipts agree between accounting and the management system and explains the differences that remain. The workbook compares totals by carrier, identifies amounts present in only one report and suggests matches when the same carrier appears under different names. Deposits without supporting statements are listed for follow-up, and confirmed name matches are saved for the next reconciliation. Matching totals show agreement between the two reports; they do not prove that every commission was received or that the carrier paid the correct rate.',
      examples: [
        'A $4,657 gap between the books and the management system, narrowed to ten named carriers',
        'A carrier booked under two names on one side and one on the other',
        'Six carriers recorded with no statement on hand, totalled, with the requests drafted'
      ]
    },
    icon: ReceiptPercentIcon,
    pro: true,
    sources: [
      {
        label: 'Accounting report',
        description:
          'Commission received by carrier, from accounting or the bookkeeper: a QuickBooks summary, a deposit report, a cash receipts export.',
        filePrefix: '[Accounting Report] ',
        suppliedMessageKey: 'Accounting Report Supplied'
      },
      {
        label: 'Management system report',
        description:
          'A commission summary from the management system for the same period, totalled by carrier.',
        filePrefix: '[Management System Report] ',
        suppliedMessageKey: 'Management Report Supplied'
      },
      {
        label: 'Carrier statements',
        requirement: 'optional',
        // Optional here and required on the other three tiles, because this run
        // uses statements as the evidence that settles a particular carrier's
        // difference. A carrier holding money with no statement behind it is
        // the finding, so the lane being short is itself informative.
        description:
          'The statements on hand. A statement is what turns a difference into an explained one, and a carrier with money and no statement becomes a request.',
        filePrefix: '[Statement] '
      },
      {
        label: 'Other supporting information',
        requirement: 'optional',
        description:
          'Anything else that helps explain a difference. Most useful is a list pairing up the names a carrier goes by, where the management system, the accounting report and the statements each call it something different.',
        filePrefix: '[Supporting Document] ',
        suppliedMessageKey: 'Supporting Information Supplied'
      }
    ],
    instructions: {
      label: 'Instructions',
      placeholder: 'Optional. Carrier names that go together, deposits that are not commission'
    },
    // The workbook is the deliverable rather than a format choice: the sheet is
    // what the agency works through, writes its own findings into, and uploads
    // again next month. The other three tiles default to the write-up because
    // their answer is readable in the workspace.
    deliverable: benefitsWorkbook('xlsx'),
    requires: 'sources'
  }
];

/** Reachable use cases. `USE_CASES` also includes entries no surface should route to. */
export const AVAILABLE_USE_CASES = USE_CASES.filter((useCase) => !useCase.unavailable);

export const hasRequiredLibrary = (
  useCase: Pick<UseCaseSpec, 'requiresLibrary'>,
  owned: Partial<Record<RunCardTemplateKind, { length: number }>>
) =>
  !useCase.requiresLibrary ||
  useCase.requiresLibrary.some((kind) => (owned[kind]?.length ?? 0) > 0);

const BY_ID = new Map(USE_CASES.map((useCase) => [useCase.id, useCase]));

export const useCaseById = (id: CaraWorkspaceUseCase): UseCaseSpec | undefined => BY_ID.get(id);

/** Follows one `supersededBy` hop. Undefined if the id is unknown or has no successor. */
export const resolveRequestedUseCase = (id: CaraWorkspaceUseCase): UseCaseSpec | undefined => {
  const requested = BY_ID.get(id);
  if (!requested?.unavailable) {
    return requested;
  }
  const successor = requested.supersededBy ? BY_ID.get(requested.supersededBy) : undefined;
  return successor?.unavailable ? undefined : successor;
};

export const familyForPreset = (preset: CaraWorkspacePreset) =>
  USE_CASE_FAMILIES.find((family) => family.preset === preset);

/**
 * The category every use case of a preset shares, or undefined when they span
 * more than one, for a surface that knows the preset but not the use case.
 */
export const categoryForPreset = (preset: CaraWorkspacePreset): UseCaseCategory | undefined => {
  const categories = new Set(
    USE_CASES.filter((useCase) => useCase.preset === preset).map((useCase) => useCase.category)
  );
  return categories.size === 1 ? [...categories][0] : undefined;
};

/** Precomputed search corpus, including about-text terms that never appear in titles. */
const SEARCH_TEXT: Map<CaraWorkspaceUseCase, string> = new Map(
  USE_CASES.map((useCase) => [
    useCase.id,
    [
      useCase.title,
      useCase.description,
      familyForPreset(useCase.preset)?.title ?? '',
      useCase.about.summary,
      ...useCase.about.examples,
      ...(useCase.deliverable?.choices ?? []).map((choice) => choice.label),
      useCase.documents?.label ?? ''
    ]
      .join(' ')
      .toLowerCase()
  ])
);

export const searchTerms = (query: string): string[] =>
  query.trim().toLowerCase().split(/\s+/).filter(Boolean);

/** Title matches rank above description; zero means no term hit. */
export const useCaseSearchScore = (useCase: UseCaseSpec, terms: string[]): number => {
  if (terms.length === 0) {
    return 1;
  }
  const text = SEARCH_TEXT.get(useCase.id) ?? '';
  if (!terms.every((term) => text.includes(term))) {
    return 0;
  }
  const title = useCase.title.toLowerCase();
  if (terms.every((term) => title.includes(term))) {
    return 3;
  }
  const stated = `${useCase.title} ${useCase.description}`.toLowerCase();
  return terms.every((term) => stated.includes(term)) ? 2 : 1;
};

export const useCasesInFamily = (preset: CaraWorkspacePreset): UseCaseSpec[] =>
  AVAILABLE_USE_CASES.filter((useCase) => useCase.preset === preset);

/** Filters the shared `acordDocumentOptions` list using the legacy use-case ids it is tagged with. */
export const acordDocumentsForScope = (scope: DocumentPickerScope) => {
  const legacyId =
    scope === 'certificates'
      ? 'generate_certificate'
      : scope === 'idCards'
        ? 'generate_id_cards'
        : 'fill_acord_form';

  return acordDocumentOptions.filter((option) => {
    if (scope === 'certificates' && option.hiddenForGenerateCertificate) {
      return false;
    }
    return !option.useCases || option.useCases.includes(legacyId as UseCase);
  });
};
